import { PromisePool } from '@supercharge/promise-pool';
import { fetchMiniMetadatas, fetchInitMiniMetadatas, availableTokenLogoFilenames, githubTokenLogoUrl } from '@talismn/chaindata-provider';
import { Dexie, liveQuery } from 'dexie';
import isEqual from 'lodash/isEqual';
import { from, Observable, scan, share, map, switchAll, combineLatest } from 'rxjs';
import anylogger from 'anylogger';
import { BigMath, isArrayOf, isBigInt, planckToTokens, isEthereumAddress, hasOwnProperty, decodeAnyAddress, Deferred, blake2Concat } from '@talismn/util';
import { u8aToHex, assert, BN, u8aConcat, stringToU8a, hexToU8a, bnToU8a, u8aToString, u8aConcatStrict, hexToNumber } from '@polkadot/util';
import { twox64, $metadataV14, getShape, getMetadataVersion, filterMetadataPalletsAndItems, transformMetadataV14 } from '@talismn/scale';
import { parseAbi, isHex, hexToBigInt } from 'viem';
import { decorateStorage, StorageKey, Metadata, TypeRegistry, createType } from '@polkadot/types';
import { defineMethod } from '@substrate/txwrapper-core';
import * as $ from '@talismn/subshape-fork';
import camelCase from 'lodash/camelCase';
import groupBy from 'lodash/groupBy';
import { blake2AsU8a } from '@polkadot/util-crypto';
import upperFirst from 'lodash/upperFirst';
import { Abi } from '@polkadot/api-contract';

// TODO: Document default balances module purpose/usage
const DefaultBalanceModule = type => ({
  get type() {
    return type;
  },
  async fetchSubstrateChainMeta() {
    return null;
  },
  async fetchEvmChainMeta() {
    return null;
  },
  async fetchSubstrateChainTokens() {
    return Promise.resolve({});
  },
  async fetchEvmChainTokens() {
    return Promise.resolve({});
  },
  async subscribeBalances(_, callback) {
    callback(new Error("Balance subscriptions are not implemented in this module."));
    return () => {};
  },
  getPlaceholderBalance() {
    throw new Error("Balance placeholder is not implemented in this module.");
  },
  async fetchBalances() {
    throw new Error("Balance fetching is not implemented in this module.");
  },
  async transferToken() {
    throw new Error("Token transfers are not implemented in this module.");
  }
});

//
// internal
//

/**
 * Fetches tokens for EVM networks.
 */
class EvmTokenFetcher {
  #chaindataProvider;
  #balanceModules;
  constructor(chaindataProvider, balanceModules) {
    this.#chaindataProvider = chaindataProvider;
    this.#balanceModules = balanceModules;
  }
  async update(evmNetworkIds) {
    await this.updateEvmNetworks(evmNetworkIds);
  }
  async updateEvmNetworks(evmNetworkIds) {
    const evmNetworks = new Map((await this.#chaindataProvider.evmNetworks()).map(evmNetwork => [evmNetwork.id, evmNetwork]));
    const allEvmTokens = {};
    const evmNetworkConcurrency = 10;
    await PromisePool.withConcurrency(evmNetworkConcurrency).for(evmNetworkIds).process(async evmNetworkId => {
      const evmNetwork = evmNetworks.get(evmNetworkId);
      if (!evmNetwork) return;
      for (const mod of this.#balanceModules.filter(m => m.type.startsWith("evm-"))) {
        const balancesConfig = (evmNetwork.balancesConfig ?? []).find(({
          moduleType
        }) => moduleType === mod.type);
        const moduleConfig = balancesConfig?.moduleConfig ?? {};

        // chainMeta arg only needs the isTestnet property, let's save a db roundtrip for now
        const isTestnet = evmNetwork.isTestnet ?? false;
        const tokens = await mod.fetchEvmChainTokens(evmNetworkId, {
          isTestnet
        }, moduleConfig);
        for (const [tokenId, token] of Object.entries(tokens)) allEvmTokens[tokenId] = token;
      }
    });
    await this.#chaindataProvider.updateEvmNetworkTokens(Object.values(allEvmTokens));
  }
}

var packageJson = {
	name: "@talismn/balances",
	version: "0.0.0-pr1378-20240327183927",
	author: "Talisman",
	homepage: "https://talisman.xyz",
	license: "GPL-3.0-or-later",
	publishConfig: {
		access: "public"
	},
	repository: {
		directory: "packages/balances",
		type: "git",
		url: "https://github.com/talismansociety/talisman.git"
	},
	main: "dist/talismn-balances.cjs.js",
	module: "dist/talismn-balances.esm.js",
	files: [
		"/dist",
		"/plugins"
	],
	engines: {
		node: ">=18"
	},
	scripts: {
		test: "jest",
		lint: "eslint src --max-warnings 0",
		clean: "rm -rf dist && rm -rf .turbo rm -rf node_modules"
	},
	dependencies: {
		"@supercharge/promise-pool": "^3.1.0",
		"@talismn/chain-connector": "workspace:*",
		"@talismn/chain-connector-evm": "workspace:*",
		"@talismn/chaindata-provider": "workspace:*",
		"@talismn/scale": "workspace:*",
		"@talismn/subshape-fork": "^0.0.2",
		"@talismn/token-rates": "workspace:*",
		"@talismn/util": "workspace:*",
		anylogger: "^1.0.11",
		dexie: "^3.2.4",
		lodash: "4.17.21",
		rxjs: "^7.8.1",
		viem: "^2.8.18"
	},
	devDependencies: {
		"@polkadot/api-contract": "^10.9.1",
		"@polkadot/types": "^10.9.1",
		"@polkadot/util": "^12.3.2",
		"@polkadot/util-crypto": "^12.3.2",
		"@substrate/txwrapper-core": "*",
		"@talismn/eslint-config": "workspace:*",
		"@talismn/tsconfig": "workspace:*",
		"@types/jest": "^27.5.1",
		"@types/lodash": "^4.14.180",
		eslint: "^8.52.0",
		jest: "^29.7.0",
		"ts-jest": "^29.1.1",
		typescript: "^5.2.2"
	},
	peerDependencies: {
		"@polkadot/api-contract": "10.x",
		"@polkadot/types": "10.x",
		"@polkadot/util": "12.x",
		"@polkadot/util-crypto": "12.x",
		"@substrate/txwrapper-core": "4.x"
	},
	preconstruct: {
		entrypoints: [
			"index.ts",
			"plugins.ts"
		]
	},
	eslintConfig: {
		root: true,
		"extends": [
			"@talismn/eslint-config/base"
		]
	}
};

var log = anylogger(packageJson.name);

/**
 * `BalanceTypes` is an automatically determined sub-selection of `PluginBalanceTypes`.
 *
 * It is the same list, but with any invalid `BalanceType` definitions filtered out.
 */

/**
 * The `BalanceJson` sum type, which is a union of all of the possible `BalanceTypes`.
 *
 * Each variant comes from a plugin in use by the consuming app.
 *
 * For example, in an app with the `substrate-native`, `evm-native`, `substrate-tokens` and `evm-erc20` plugins:
 *
 *     type BalanceJson = SubNativeBalance | EvmNativeBalance | SubTokensBalance | EvmErc20Balance
 *
 * If `BalanceTypes` is empty then `BalanceJson` will fall back to the common `IBalance` interface, which every balance must implement.
 */

/** A collection of `BalanceJson` objects */

const BalanceStatusLive = subscriptionId => `live-${subscriptionId}`;

/** `IBalance` is a common interface which all balance types must implement. */

/** An unlabelled amount of a balance */

/** A labelled amount of a balance */

/** A labelled locked amount of a balance */

function excludeFromTransferableAmount(locks) {
  if (typeof locks === "string") return BigInt(locks);
  if (!Array.isArray(locks)) locks = [locks];
  return locks.filter(lock => lock.includeInTransferable !== true).map(lock => BigInt(lock.amount)).reduce((max, lock) => BigMath.max(max, lock), 0n);
}
function excludeFromFeePayableLocks(locks) {
  if (typeof locks === "string") return [];
  if (!Array.isArray(locks)) locks = [locks];
  return locks.filter(lock => lock.excludeFromFeePayable);
}

/** A labelled extra amount of a balance */

function includeInTotalExtraAmount(extra) {
  if (!extra) return 0n;
  if (!Array.isArray(extra)) extra = [extra];
  return extra.filter(extra => extra.includeInTotal).map(extra => BigInt(extra.amount)).reduce((a, b) => a + b, 0n);
}

/** Used by plugins to help define their custom `BalanceType` */

/**
 * Have the importing library define its Token and BalanceJson enums (as a sum type of all plugins) and pass them into some
 * internal global typescript context, which is then picked up on by this module.
 */

/** A utility type used to extract the underlying `BalanceType` of a specific source from a generalised `BalanceJson` */
/** TODO: Remove this in favour of a frontend-friendly `ChaindataProvider` */
/**
 * A collection of balances.
 */
class Balances {
  //
  // Properties
  //

  #balances = [];

  //
  // Methods
  //

  constructor(balances, hydrate) {
    // handle Balances (convert to Balance[])
    if (balances instanceof Balances) return new Balances(balances.each, hydrate);

    // handle Balance (convert to Balance[])
    if (balances instanceof Balance) return new Balances([balances], hydrate);

    // handle BalanceJsonList (the only remaining non-array type of balances) (convert to BalanceJson[])
    if (!Array.isArray(balances)) return new Balances(Object.values(balances), hydrate);

    // handle no balances
    if (balances.length === 0) return this;

    // handle BalanceJson[]
    if (!isArrayOf(balances, Balance)) return new Balances(balances.map(storage => new Balance(storage)), hydrate);

    // handle Balance[]
    this.#balances = balances;
    if (hydrate !== undefined) this.hydrate(hydrate);
  }

  /**
   * Calling toJSON on a collection of balances will return the underlying BalanceJsonList.
   */
  toJSON = () => Object.fromEntries(this.#balances.map(balance => {
    try {
      return [balance.id, balance.toJSON()];
    } catch (error) {
      log.error("Failed to convert balance to JSON", error, {
        id: balance.id,
        balance
      });
      return null;
    }
  }).filter(Array.isArray));

  /**
   * Allows the collection to be iterated over.
   *
   * @example
   * [...balances].forEach(balance => { // do something // })
   *
   * @example
   * for (const balance of balances) {
   *   // do something
   * }
   */
  [Symbol.iterator] = () =>
  // Create an array of the balances in this collection and return the result of its iterator.
  this.#balances[Symbol.iterator]();

  /**
   * Hydrates all balances in this collection.
   *
   * @param sources - The sources to hydrate from.
   */
  hydrate = sources => {
    this.#balances.map(balance => balance.hydrate(sources));
  };

  /**
   * Retrieve a balance from this collection by id.
   *
   * @param id - The id of the balance to fetch.
   * @returns The balance if one exists, or none.
   */
  get = id => this.#balances.find(balance => balance.id === id) ?? null;

  /**
   * Retrieve balances from this collection by search query.
   *
   * @param query - The search query.
   * @returns All balances which match the query.
   */
  find = query => {
    // construct filter
    const orQueries = (Array.isArray(query) ? query : [query]).map(query => typeof query === "function" ? query : Object.entries(query));

    // filter balances
    const filter = balance => orQueries.some(query => typeof query === "function" ? query(balance) : query.every(([key, value]) => balance[key] === value));

    // return filter matches
    return new Balances([...this].filter(filter));
  };

  /**
   * Filters this collection to exclude token balances where the token has a `mirrorOf` field
   * and another balance exists in this collection for the token specified by the `mirrorOf` field.
   */
  filterMirrorTokens = () => new Balances([...this].filter(filterMirrorTokens));

  /**
   * Filters this collection to only include balances which are not zero.
   */
  filterNonZero = type => {
    const filter = balance => balance[type].planck > 0n;
    return this.find(filter);
  };
  /**
   * Filters this collection to only include balances which are not zero AND have a fiat conversion rate.
   */
  filterNonZeroFiat = (type, currency) => {
    const filter = balance => (balance[type].fiat(currency) ?? 0) > 0;
    return this.find(filter);
  };

  /**
   * Add some balances to this collection.
   * Added balances take priority over existing balances.
   * The aggregation of the two collections is returned.
   * The original collection is not mutated.
   *
   * @param balances - Either a balance or collection of balances to add.
   * @returns The new collection of balances.
   */
  add = balances => {
    // handle single balance
    if (balances instanceof Balance) return this.add(new Balances(balances));

    // merge balances
    const mergedBalances = Object.fromEntries(this.#balances.map(balance => [balance.id, balance]));
    balances.each.forEach(balance => mergedBalances[balance.id] = balance);

    // return new balances
    return new Balances(Object.values(mergedBalances));
  };

  /**
   * Remove balances from this collection by id.
   * A new collection without these balances is returned.
   * The original collection is not mutated.
   *
   * @param ids - The id(s) of the balances to remove.
   * @returns The new collection of balances.
   */
  remove = ids => {
    // handle single id
    if (!Array.isArray(ids)) return this.remove([ids]);

    // merge and return new balances
    return new Balances(this.#balances.filter(balance => !ids.includes(balance.id)));
  };

  // TODO: Add some more useful aggregator methods

  get each() {
    return [...this];
  }

  /**
   * Get an array of balances in this collection, sorted by chain sortIndex.
   *
   * @returns A sorted array of the balances in this collection.
   */
  get sorted() {
    return [...this].sort((a, b) => ((a.chain || a.evmNetwork)?.sortIndex ?? Number.MAX_SAFE_INTEGER) - ((b.chain || b.evmNetwork)?.sortIndex ?? Number.MAX_SAFE_INTEGER));
  }

  /**
   * Get the number of balances in this collection.
   *
   * @returns The number of balances in this collection.
   */
  get count() {
    return [...this].length;
  }

  /**
   * Get the summed value of balances in this collection.
   * TODO: Sum up token amounts AND fiat amounts
   *
   * @example
   * // Get the sum of all transferable balances in usd.
   * balances.sum.fiat('usd').transferable
   */
  get sum() {
    return new SumBalancesFormatter(this);
  }
}

/**
 * An individual balance.
 */
class Balance {
  //
  // Properties
  //

  /** The underlying data for this balance */
  #storage;
  #db = null;

  //
  // Methods
  //

  constructor(storage, hydrate) {
    this.#storage = storage;
    if (hydrate !== undefined) this.hydrate(hydrate);
  }
  toJSON = () => this.#storage;
  isSource = source => this.#storage.source === source;
  // // TODO: Fix this method, the types don't work with our plugin architecture.
  // // Specifically, the `BalanceJson` type is compiled down to `IBalance` in the following way:
  // //
  // //     toJSON: () => BalanceJson // works
  // //     isSource: (source: BalanceSource) => boolean // works
  // //     asSource: <P extends string>(source: P) => NarrowBalanceType<IBalance, P> | null // Doesn't work! IBalance should just be BalanceJson!
  // //
  // // `IBalance` won't match the type of `BalanceSource` after `PluginBalanceTypes` has been extended by balance plugins.
  // // As a result, typescript will think that the returned #storage is not a BalanceJson.
  // asSource = <P extends BalanceSource>(source: P): NarrowBalanceType<BalanceJson, P> | null => {
  //   if (this.#storage.source === source) return this.#storage as NarrowBalanceType<BalanceJson, P>
  //   return null
  // }

  hydrate = hydrate => {
    if (hydrate !== undefined) this.#db = hydrate;
  };
  #format = balance => new BalanceFormatter(isBigInt(balance) ? balance.toString() : balance, this.decimals || undefined, this.#db?.tokenRates && this.#db.tokenRates[this.tokenId]);

  //
  // Accessors
  //

  get id() {
    const {
      source,
      subSource,
      address,
      chainId,
      evmNetworkId,
      tokenId
    } = this.#storage;
    const locationId = chainId !== undefined ? chainId : evmNetworkId;
    return [source, address, locationId, tokenId, subSource].filter(Boolean).join("-");
  }
  get source() {
    return this.#storage.source;
  }
  get subSource() {
    return this.#storage.subSource;
  }
  get status() {
    return this.#storage.status;
  }
  get address() {
    return this.#storage.address;
  }
  get chainId() {
    return this.#storage.chainId;
  }
  get chain() {
    return this.#db?.chains && this.chainId && this.#db?.chains[this.chainId] || null;
  }
  get evmNetworkId() {
    return this.#storage.evmNetworkId;
  }
  get evmNetwork() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.#db?.evmNetworks && this.#db?.evmNetworks[this.evmNetworkId] || null;
  }
  get tokenId() {
    return this.#storage.tokenId;
  }
  get token() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.#db?.tokens && this.#db?.tokens[this.tokenId] || null;
  }
  get decimals() {
    return this.token?.decimals || null;
  }
  get rates() {
    return this.#db?.tokenRates && this.#db.tokenRates[this.tokenId] || null;
  }

  /**
   * The total balance of this token.
   * Includes the free and the reserved amount.
   * The balance will be reaped if this goes below the existential deposit.
   */
  get total() {
    const extra = includeInTotalExtraAmount(this.#storage.extra);
    return this.#format(this.free.planck + this.reserved.planck + extra);
  }
  /** The non-reserved balance of this token. Includes the frozen amount. Is included in the total. */
  get free() {
    return this.#format(typeof this.#storage.free === "string" ? BigInt(this.#storage.free) : Array.isArray(this.#storage.free) ? this.#storage.free.map(reserve => BigInt(reserve.amount)).reduce((a, b) => a + b, 0n) : BigInt(this.#storage.free?.amount || "0"));
  }
  /** The reserved balance of this token. Is included in the total. */
  get reserved() {
    return this.#format(typeof this.#storage.reserves === "string" ? BigInt(this.#storage.reserves) : Array.isArray(this.#storage.reserves) ? this.#storage.reserves.map(reserve => BigInt(reserve.amount)).reduce((a, b) => a + b, 0n) : BigInt(this.#storage.reserves?.amount || "0"));
  }
  get reserves() {
    return (Array.isArray(this.#storage.reserves) ? this.#storage.reserves : [this.#storage.reserves]).flatMap(reserve => {
      if (reserve === undefined) return [];
      if (typeof reserve === "string") return {
        label: "reserved",
        amount: this.#format(reserve)
      };
      return {
        ...reserve,
        amount: this.#format(reserve.amount)
      };
    });
  }
  /** The frozen balance of this token. Is included in the free amount. */
  get locked() {
    return this.#format(typeof this.#storage.locks === "string" ? BigInt(this.#storage.locks) : Array.isArray(this.#storage.locks) ? this.#storage.locks.map(lock => BigInt(lock.amount)).reduce((a, b) => BigMath.max(a, b), 0n) : BigInt(this.#storage.locks?.amount || "0"));
  }
  get locks() {
    return (Array.isArray(this.#storage.locks) ? this.#storage.locks : [this.#storage.locks]).flatMap(lock => {
      if (lock === undefined) return [];
      if (typeof lock === "string") return {
        label: "other",
        amount: this.#format(lock)
      };
      return {
        ...lock,
        amount: this.#format(lock.amount)
      };
    });
  }
  /** @deprecated Use balance.locked */
  get frozen() {
    return this.locked;
  }
  /** The transferable balance of this token. Is generally the free amount - the miscFrozen amount. */
  get transferable() {
    // if no locks exist, transferable is equal to the free amount
    if (!this.#storage.locks) return this.free;

    // find the largest lock (but ignore any locks which are marked as `includeInTransferable`)
    const excludeAmount = excludeFromTransferableAmount(this.#storage.locks);

    // subtract the lock from the free amount (but don't go below 0)
    return this.#format(BigMath.max(this.free.planck - excludeAmount, 0n));
  }
  /** The feePayable balance of this token. Is generally the free amount - the feeFrozen amount. */
  get feePayable() {
    // if no locks exist, feePayable is equal to the free amount
    if (!this.#storage.locks) return this.free;

    // find the largest lock which can't be used to pay tx fees
    const excludeAmount = excludeFromFeePayableLocks(this.#storage.locks).map(lock => BigInt(lock.amount)).reduce((max, lock) => BigMath.max(max, lock), 0n);

    // subtract the lock from the free amount (but don't go below 0)
    return this.#format(BigMath.max(this.free.planck - excludeAmount, 0n));
  }
}
class BalanceFormatter {
  #planck;
  #decimals;
  #fiatRatios;
  constructor(planck, decimals, fiatRatios) {
    this.#planck = isBigInt(planck) ? planck.toString() : planck ?? "0";
    this.#decimals = decimals || 0;
    this.#fiatRatios = fiatRatios || null;
  }
  toJSON = () => this.#planck;
  get planck() {
    return BigInt(this.#planck);
  }
  get tokens() {
    return planckToTokens(this.#planck, this.#decimals);
  }
  fiat(currency) {
    if (!this.#fiatRatios) return null;
    const ratio = this.#fiatRatios[currency];
    if (!ratio) return null;
    return parseFloat(this.tokens) * ratio;
  }
}
class PlanckSumBalancesFormatter {
  #balances;
  constructor(balances) {
    this.#balances = balances;
  }
  #sum = balanceField => {
    // a function to get a planck amount from a balance
    const planck = balance => balance[balanceField].planck ?? 0n;
    return this.#balances.filterMirrorTokens().each.reduce(
    // add the total amount to the planck amount of each balance
    (total, balance) => total + planck(balance),
    // start with a total of 0
    0n);
  };

  /**
   * The total balance of these tokens. Includes the free and the reserved amount.
   */
  get total() {
    return this.#sum("total");
  }
  /** The non-reserved balance of these tokens. Includes the frozen amount. Is included in the total. */
  get free() {
    return this.#sum("free");
  }
  /** The reserved balance of these tokens. Is included in the total. */
  get reserved() {
    return this.#sum("reserved");
  }
  /** The frozen balance of these tokens. Is included in the free amount. */
  get locked() {
    return this.#sum("locked");
  }
  /** @deprecated Use balances.locked */
  get frozen() {
    return this.locked;
  }
  /** The transferable balance of these tokens. Is generally the free amount - the miscFrozen amount. */
  get transferable() {
    return this.#sum("transferable");
  }
  /** The feePayable balance of these tokens. Is generally the free amount - the feeFrozen amount. */
  get feePayable() {
    return this.#sum("feePayable");
  }
}
class FiatSumBalancesFormatter {
  #balances;
  #currency;
  constructor(balances, currency) {
    this.#balances = balances;
    this.#currency = currency;
  }
  #sum = balanceField => {
    // a function to get a fiat amount from a balance
    const fiat = balance => balance[balanceField].fiat(this.#currency) ?? 0;
    return this.#balances.filterMirrorTokens().each.reduce(
    // add the total amount to the fiat amount of each balance
    (total, balance) => total + fiat(balance),
    // start with a total of 0
    0);
  };

  /**
   * The total balance of these tokens. Includes the free and the reserved amount.
   */
  get total() {
    return this.#sum("total");
  }
  /** The non-reserved balance of these tokens. Includes the frozen amount. Is included in the total. */
  get free() {
    return this.#sum("free");
  }
  /** The reserved balance of these tokens. Is included in the total. */
  get reserved() {
    return this.#sum("reserved");
  }
  /** The frozen balance of these tokens. Is included in the free amount. */
  get locked() {
    return this.#sum("locked");
  }
  /** @deprecated Use balances.locked */
  get frozen() {
    return this.locked;
  }
  /** The transferable balance of these tokens. Is generally the free amount - the miscFrozen amount. */
  get transferable() {
    return this.#sum("transferable");
  }
  /** The feePayable balance of these tokens. Is generally the free amount - the feeFrozen amount. */
  get feePayable() {
    return this.#sum("feePayable");
  }
}
class SumBalancesFormatter {
  #balances;
  constructor(balances) {
    this.#balances = balances;
  }
  get planck() {
    return new PlanckSumBalancesFormatter(this.#balances);
  }
  fiat(currency) {
    return new FiatSumBalancesFormatter(this.#balances, currency);
  }
}
const filterMirrorTokens = (balance, i, balances) => {
  const mirrorOf = balance.token?.mirrorOf;
  return !mirrorOf || !balances.find(b => b.tokenId === mirrorOf);
};

/** For fast db access, you can calculate the primary key for a miniMetadata using this method */
const deriveMiniMetadataId = ({
  source,
  chainId,
  specName,
  specVersion,
  balancesConfig
}) => u8aToHex(twox64.hash(new TextEncoder().encode(`${source}${chainId}${specName}${specVersion}${balancesConfig}`)), undefined, false);

// for DB version 3, Wallet version 1.21.0
const upgradeRemoveSymbolFromNativeTokenId = async tx => {
  const balancesTable = tx.table("balances");
  try {
    await balancesTable.toCollection().modify(balance => {
      if (balance?.tokenId?.includes?.("-substrate-native-")) {
        balance.tokenId = balance.tokenId.replace(/-substrate-native-.+$/, "-substrate-native");
        balance.id = new Balance(balance).id;
      }
      if (balance?.tokenId?.includes?.("-evm-native-")) {
        balance.tokenId = balance.tokenId.replace(/-evm-native-.+$/, "-evm-native");
        balance.id = new Balance(balance).id;
      }
    });
  } catch (err) {
    // error most likely due to duplicate ids => clear the db
    log.error("Failed to upgrade balances native token ids", {
      err
    });
    await balancesTable.clear();
  }
};

class TalismanBalancesDatabase extends Dexie {
  constructor() {
    super("TalismanBalances");

    // https://dexie.org/docs/Tutorial/Design#database-versioning
    this.version(3).stores({
      // You only need to specify properties that you wish to index.
      // The object store will allow any properties on your stored objects but you can only query them by indexed properties
      // https://dexie.org/docs/API-Reference#declare-database
      //
      // Never index properties containing images, movies or large (huge) strings. Store them in IndexedDB, yes! but just don’t index them!
      // https://dexie.org/docs/Version/Version.stores()#warning
      balances: "id, source, status, address, tokenId",
      miniMetadatas: "id, source, chainId, specName, specVersion"
    }).upgrade(upgradeRemoveSymbolFromNativeTokenId);
  }
}
const db = new TalismanBalancesDatabase();

const minimumHydrationInterval = 300_000; // 300_000ms = 300s = 5 minutes

/**
 * A substrate dapp needs access to a set of types when it wants to communicate with a blockchain node.
 *
 * These types are used to encode requests & decode responses via the SCALE codec.
 * Each chain generally has its own set of types.
 *
 * Substrate provides a construct to retrieve these types from a blockchain node.
 * The chain metadata.
 *
 * The metadata includes the types required for any communication with the chain,
 * including lots of methods which are not relevant to balance fetching.
 *
 * As such, the metadata can clock in at around 1-2MB per chain, which is a lot of storage
 * for browser-based dapps which want to connect to lots of chains.
 *
 * By utilizing the wonderful [subshape](https://github.com/paritytech/subshape) library,
 * we can trim the chain metadata down so that it only includes the types we need for balance fetching.
 *
 * Each balance module has a function to do just that, `BalanceModule::fetchSubstrateChainMeta`.
 *
 * But, we only want to run this operation when necessary.
 *
 * The purpose of this class, `MiniMetadataUpdater`, is to maintain a local cache of
 * trimmed-down metadatas, which we'll refer to as `MiniMetadatas`.
 */
class MiniMetadataUpdater {
  #lastHydratedMiniMetadatasAt = 0;
  #lastHydratedCustomChainsAt = 0;
  #chainConnectors;
  #chaindataProvider;
  #balanceModules;
  constructor(chainConnectors, chaindataProvider, balanceModules) {
    this.#chainConnectors = chainConnectors;
    this.#chaindataProvider = chaindataProvider;
    this.#balanceModules = balanceModules;
  }

  /** Subscribe to the metadata for a chain */
  subscribe(chainId) {
    return from(liveQuery(() => db.miniMetadatas.filter(m => m.chainId === chainId).toArray().then(array => array[0])));
  }
  async update(chainIds) {
    await this.updateSubstrateChains(chainIds);
  }
  async statuses(chains) {
    const ids = await db.miniMetadatas.orderBy("id").primaryKeys();
    const wantedIdsByChain = new Map(chains.flatMap(({
      id: chainId,
      specName,
      specVersion,
      balancesConfig
    }) => {
      if (specName === null) return [];
      if (specVersion === null) return [];
      return [[chainId, this.#balanceModules.filter(m => m.type.startsWith("substrate-")).map(({
        type: source
      }) => deriveMiniMetadataId({
        source,
        chainId: chainId,
        specName: specName,
        specVersion: specVersion,
        balancesConfig: JSON.stringify((balancesConfig ?? []).find(({
          moduleType
        }) => moduleType === source)?.moduleConfig ?? {})
      }))]];
    }));
    const statusesByChain = new Map(Array.from(wantedIdsByChain.entries()).map(([chainId, wantedIds]) => [chainId, wantedIds.every(wantedId => ids.includes(wantedId)) ? "good" : "none"]));
    return {
      wantedIdsByChain,
      statusesByChain
    };
  }
  async hydrateFromChaindata() {
    const now = Date.now();
    if (now - this.#lastHydratedMiniMetadatasAt < minimumHydrationInterval) return false;
    const dbHasMiniMetadatas = (await db.miniMetadatas.count()) > 0;
    try {
      try {
        var miniMetadatas = await fetchMiniMetadatas(); // eslint-disable-line no-var
        if (miniMetadatas.length <= 0) throw new Error("Ignoring empty chaindata miniMetadatas response");
      } catch (error) {
        if (dbHasMiniMetadatas) throw error;
        // On first start-up (db is empty), if we fail to fetch miniMetadatas then we should
        // initialize the DB with the list of miniMetadatas inside our init/mini-metadatas.json file.
        // This data will represent a relatively recent copy of what's in chaindata,
        // which will be better for our users than to have nothing at all.
        var miniMetadatas = await fetchInitMiniMetadatas(); // eslint-disable-line no-var
      }

      await db.miniMetadatas.bulkPut(miniMetadatas);
      this.#lastHydratedMiniMetadatasAt = now;
      return true;
    } catch (error) {
      log.warn(`Failed to hydrate miniMetadatas from chaindata`, error);
      return false;
    }
  }
  async hydrateCustomChains() {
    const now = Date.now();
    if (now - this.#lastHydratedCustomChainsAt < minimumHydrationInterval) return false;
    const chains = await this.#chaindataProvider.chains();
    const customChains = chains.filter(chain => "isCustom" in chain && chain.isCustom);
    const updatedCustomChains = [];
    const concurrency = 4;
    (await PromisePool.withConcurrency(concurrency).for(customChains).process(async customChain => {
      const send = (method, params) => this.#chainConnectors.substrate?.send(customChain.id, method, params);
      const [genesisHash, runtimeVersion, chainName, chainType] = await Promise.all([send("chain_getBlockHash", [0]), send("state_getRuntimeVersion", []), send("system_chain", []), send("system_chainType", [])]);

      // deconstruct rpc data
      const {
        specName,
        implName
      } = runtimeVersion;
      const specVersion = String(runtimeVersion.specVersion);
      const changed = customChain.genesisHash !== genesisHash || customChain.chainName !== chainName || !isEqual(customChain.chainType, chainType) || customChain.implName !== implName || customChain.specName !== specName || customChain.specVersion !== specVersion;
      if (!changed) return;
      customChain.genesisHash = genesisHash;
      customChain.chainName = chainName;
      customChain.chainType = chainType;
      customChain.implName = implName;
      customChain.specName = specName;
      customChain.specVersion = specVersion;
      updatedCustomChains.push(customChain);
    })).errors.forEach(error => log.error("Error hydrating custom chains", error));
    if (updatedCustomChains.length > 0) {
      await this.#chaindataProvider.transaction("rw", ["chains"], async () => {
        for (const updatedCustomChain of updatedCustomChains) {
          await this.#chaindataProvider.removeCustomChain(updatedCustomChain.id);
          await this.#chaindataProvider.addCustomChain(updatedCustomChain);
        }
      });
    }
    if (updatedCustomChains.length > 0) this.#lastHydratedCustomChainsAt = now;
    return true;
  }
  async updateSubstrateChains(chainIds) {
    const chains = new Map((await this.#chaindataProvider.chains()).map(chain => [chain.id, chain]));
    const filteredChains = chainIds.flatMap(chainId => chains.get(chainId) ?? []);
    const ids = await db.miniMetadatas.orderBy("id").primaryKeys();
    const {
      wantedIdsByChain,
      statusesByChain
    } = await this.statuses(filteredChains);

    // clean up store
    const wantedIds = Array.from(wantedIdsByChain.values()).flatMap(ids => ids);
    const unwantedIds = ids.filter(id => !wantedIds.includes(id));
    if (unwantedIds.length > 0) {
      const chainIds = Array.from(new Set((await db.miniMetadatas.bulkGet(unwantedIds)).map(m => m?.chainId)));
      log.info(`Pruning ${unwantedIds.length} miniMetadatas on chains ${chainIds.join(", ")}`);
      await db.miniMetadatas.bulkDelete(unwantedIds);
    }
    const needUpdates = Array.from(statusesByChain.entries()).filter(([, status]) => status !== "good").map(([chainId]) => chainId);
    if (needUpdates.length > 0) log.info(`${needUpdates.length} miniMetadatas need updates (${needUpdates.join(", ")})`);
    const availableTokenLogos = await availableTokenLogoFilenames().catch(error => {
      log.error("Failed to fetch available token logos", error);
      return [];
    });
    const concurrency = 4;
    (await PromisePool.withConcurrency(concurrency).for(needUpdates).process(async chainId => {
      log.info(`Updating metadata for chain ${chainId}`);
      const chain = chains.get(chainId);
      if (!chain) return;
      const {
        specName,
        specVersion
      } = chain;
      if (specName === null) return;
      if (specVersion === null) return;
      const metadataRpc = await this.#chainConnectors.substrate?.send(chainId, "state_getMetadata", []);
      for (const mod of this.#balanceModules.filter(m => m.type.startsWith("substrate-"))) {
        const balancesConfig = (chain.balancesConfig ?? []).find(({
          moduleType
        }) => moduleType === mod.type);
        const moduleConfig = balancesConfig?.moduleConfig ?? {};
        const metadata = await mod.fetchSubstrateChainMeta(chainId, moduleConfig, metadataRpc);
        const tokens = await mod.fetchSubstrateChainTokens(chainId, metadata, moduleConfig);

        // update tokens in chaindata
        await this.#chaindataProvider.updateChainTokens(chainId, mod.type, Object.values(tokens), availableTokenLogos);

        // update miniMetadatas
        const {
          miniMetadata: data,
          metadataVersion: version,
          ...extra
        } = metadata ?? {};
        await db.miniMetadatas.put({
          id: deriveMiniMetadataId({
            source: mod.type,
            chainId,
            specName,
            specVersion,
            balancesConfig: JSON.stringify(moduleConfig)
          }),
          source: mod.type,
          chainId,
          specName,
          specVersion,
          balancesConfig: JSON.stringify(moduleConfig),
          // TODO: Standardise return value from `fetchSubstrateChainMeta`
          version,
          data,
          extra: JSON.stringify(extra)
        });
      }
    })).errors.forEach(error => log.error("Error updating chain metadata", error));
  }
}

const erc20Abi = [{
  constant: true,
  inputs: [],
  name: "name",
  outputs: [{
    name: "",
    type: "string"
  }],
  payable: false,
  stateMutability: "view",
  type: "function"
}, {
  constant: false,
  inputs: [{
    name: "_spender",
    type: "address"
  }, {
    name: "_value",
    type: "uint256"
  }],
  name: "approve",
  outputs: [{
    name: "",
    type: "bool"
  }],
  payable: false,
  stateMutability: "nonpayable",
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "totalSupply",
  outputs: [{
    name: "",
    type: "uint256"
  }],
  payable: false,
  stateMutability: "view",
  type: "function"
}, {
  constant: false,
  inputs: [{
    name: "_from",
    type: "address"
  }, {
    name: "_to",
    type: "address"
  }, {
    name: "_value",
    type: "uint256"
  }],
  name: "transferFrom",
  outputs: [{
    name: "",
    type: "bool"
  }],
  payable: false,
  stateMutability: "nonpayable",
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "decimals",
  outputs: [{
    name: "",
    type: "uint8"
  }],
  payable: false,
  stateMutability: "view",
  type: "function"
}, {
  constant: false,
  inputs: [{
    name: "_to",
    type: "address"
  }, {
    name: "_value",
    type: "uint256"
  }, {
    name: "_data",
    type: "bytes"
  }],
  name: "transferAndCall",
  outputs: [{
    name: "success",
    type: "bool"
  }],
  payable: false,
  stateMutability: "nonpayable",
  type: "function"
}, {
  constant: false,
  inputs: [{
    name: "_spender",
    type: "address"
  }, {
    name: "_subtractedValue",
    type: "uint256"
  }],
  name: "decreaseApproval",
  outputs: [{
    name: "success",
    type: "bool"
  }],
  payable: false,
  stateMutability: "nonpayable",
  type: "function"
}, {
  constant: true,
  inputs: [{
    name: "_owner",
    type: "address"
  }],
  name: "balanceOf",
  outputs: [{
    name: "balance",
    type: "uint256"
  }],
  payable: false,
  stateMutability: "view",
  type: "function"
}, {
  constant: true,
  inputs: [],
  name: "symbol",
  outputs: [{
    name: "",
    type: "string"
  }],
  payable: false,
  stateMutability: "view",
  type: "function"
}, {
  constant: false,
  inputs: [{
    name: "_to",
    type: "address"
  }, {
    name: "_value",
    type: "uint256"
  }],
  name: "transfer",
  outputs: [{
    name: "success",
    type: "bool"
  }],
  payable: false,
  stateMutability: "nonpayable",
  type: "function"
}, {
  constant: false,
  inputs: [{
    name: "_spender",
    type: "address"
  }, {
    name: "_addedValue",
    type: "uint256"
  }],
  name: "increaseApproval",
  outputs: [{
    name: "success",
    type: "bool"
  }],
  payable: false,
  stateMutability: "nonpayable",
  type: "function"
}, {
  constant: true,
  inputs: [{
    name: "_owner",
    type: "address"
  }, {
    name: "_spender",
    type: "address"
  }],
  name: "allowance",
  outputs: [{
    name: "remaining",
    type: "uint256"
  }],
  payable: false,
  stateMutability: "view",
  type: "function"
}, {
  inputs: [],
  payable: false,
  stateMutability: "nonpayable",
  type: "constructor"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: "from",
    type: "address"
  }, {
    indexed: true,
    name: "to",
    type: "address"
  }, {
    indexed: false,
    name: "value",
    type: "uint256"
  }, {
    indexed: false,
    name: "data",
    type: "bytes"
  }],
  name: "Transfer",
  type: "event"
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: "owner",
    type: "address"
  }, {
    indexed: true,
    name: "spender",
    type: "address"
  }, {
    indexed: false,
    name: "value",
    type: "uint256"
  }],
  name: "Approval",
  type: "event"
}];

const evmErc20TokenId = (chainId, tokenContractAddress) => `${chainId}-evm-erc20-${tokenContractAddress}`.toLowerCase();
const getEvmNetworkIdFromTokenId$1 = tokenId => {
  const evmNetworkId = tokenId.split("-")[0];
  if (!evmNetworkId) throw new Error(`Can't detect chainId for token ${tokenId}`);
  return evmNetworkId;
};
const EvmErc20Module = hydrate => {
  const {
    chainConnectors,
    chaindataProvider
  } = hydrate;
  const chainConnector = chainConnectors.evm;
  assert(chainConnector, "This module requires an evm chain connector");
  return {
    ...DefaultBalanceModule("evm-erc20"),
    /**
     * This method is currently executed on [a squid](https://github.com/TalismanSociety/chaindata-squid/blob/0ee02818bf5caa7362e3f3664e55ef05ec8df078/src/steps/updateEvmNetworksFromGithub.ts#L280-L284).
     * In a future version of the balance libraries, we may build some kind of async scheduling system which will keep the chainmeta for each chain up to date without relying on a squid.
     */
    async fetchEvmChainMeta(chainId) {
      const isTestnet = (await chaindataProvider.evmNetworkById(chainId))?.isTestnet || false;
      return {
        isTestnet
      };
    },
    /**
     * This method is currently executed on [a squid](https://github.com/TalismanSociety/chaindata-squid/blob/0ee02818bf5caa7362e3f3664e55ef05ec8df078/src/steps/updateEvmNetworksFromGithub.ts#L338-L343).
     * In a future version of the balance libraries, we may build some kind of async scheduling system which will keep the list of tokens for each chain up to date without relying on a squid.
     */
    async fetchEvmChainTokens(chainId, chainMeta, moduleConfig) {
      const {
        isTestnet
      } = chainMeta;
      const tokens = {};
      for (const tokenConfig of moduleConfig?.tokens ?? []) {
        const {
          contractAddress,
          symbol: contractSymbol,
          decimals: contractDecimals
        } = tokenConfig;
        // TODO : in chaindata's build, filter out all tokens that don't have any of these
        if (!contractAddress || !contractSymbol || contractDecimals === undefined) {
          log.warn("ignoring token on chain %s", chainId, tokenConfig);
          continue;
        }
        const symbol = tokenConfig?.symbol ?? contractSymbol ?? "ETH";
        const decimals = typeof tokenConfig?.decimals === "number" ? tokenConfig.decimals : typeof contractDecimals === "number" ? contractDecimals : 18;
        if (!symbol || typeof decimals !== "number") continue;
        const id = evmErc20TokenId(chainId, contractAddress);
        const token = {
          id,
          type: "evm-erc20",
          isTestnet,
          isDefault: tokenConfig.isDefault ?? true,
          symbol,
          decimals,
          logo: tokenConfig?.logo || githubTokenLogoUrl(id),
          contractAddress,
          evmNetwork: {
            id: chainId
          }
        };
        if (tokenConfig?.symbol) token.symbol = tokenConfig?.symbol;
        if (tokenConfig?.coingeckoId) token.coingeckoId = tokenConfig?.coingeckoId;
        if (tokenConfig?.dcentName) token.dcentName = tokenConfig?.dcentName;
        if (tokenConfig?.mirrorOf) token.mirrorOf = tokenConfig?.mirrorOf;
        tokens[token.id] = token;
      }
      return tokens;
    },
    getPlaceholderBalance(tokenId, address) {
      const evmNetworkId = getEvmNetworkIdFromTokenId$1(tokenId);
      return {
        source: "evm-erc20",
        status: "initializing",
        address: address,
        multiChainId: {
          evmChainId: evmNetworkId
        },
        evmNetworkId,
        tokenId,
        free: "0"
      };
    },
    async subscribeBalances(addressesByToken, callback) {
      let subscriptionActive = true;
      const subscriptionInterval = 6_000; // 6_000ms == 6 seconds
      const initDelay = 1_500; // 1_500ms == 1.5 seconds
      const cache = new Map();

      // TODO remove this log
      log.debug("subscribeBalances", "evm-erc20", addressesByToken);

      // for chains with a zero balance we only call fetchBalances once every 5 subscriptionIntervals
      // if subscriptionInterval is 6 seconds, this means we only poll chains with a zero balance every 30 seconds
      let zeroBalanceSubscriptionIntervalCounter = 0;
      const evmNetworks = await chaindataProvider.evmNetworksById();
      const tokens = await chaindataProvider.tokensById();
      const poll = async () => {
        if (!subscriptionActive) return;
        zeroBalanceSubscriptionIntervalCounter = (zeroBalanceSubscriptionIntervalCounter + 1) % 5;
        try {
          // regroup tokens by network
          const addressesByTokenByEvmNetwork = groupAddressesByTokenByEvmNetwork(addressesByToken, tokens);

          // fetch balance for each network sequentially to prevent creating a big queue of http requests (browser can only handle 2 at a time)
          for (const [evmNetworkId, addressesByToken] of Object.entries(addressesByTokenByEvmNetwork)) {
            const cached = cache.get(evmNetworkId);
            if (cached && zeroBalanceSubscriptionIntervalCounter !== 0 && new Balances(cached).each.reduce((sum, b) => sum + b.total.planck, 0n) === 0n) {
              // only poll empty token balances every 5 subscriptionIntervals
              continue;
            }
            try {
              if (!chainConnectors.evm) throw new Error(`This module requires an evm chain connector`);
              const balances = await fetchBalances$2(chainConnectors.evm, evmNetworks, tokens, addressesByToken);

              // Don't call callback with balances which have not changed since the last poll.
              const json = balances.toJSON();
              if (!isEqual(cached, json)) {
                cache.set(evmNetworkId, json);
                // cache contains all balances for a given network, filter out balances that didn't change
                const changes = Object.entries(json).filter(([id, balance]) => !isEqual(cached?.[id], balance));
                if (changes.length) callback(null, new Balances(Object.fromEntries(changes)));
              }
            } catch (err) {
              callback(err);
            }
          }
        } catch (error) {
          callback(error);
        } finally {
          setTimeout(poll, subscriptionInterval);
        }
      };
      setTimeout(poll, initDelay);
      return () => {
        subscriptionActive = false;
      };
    },
    async fetchBalances(addressesByToken) {
      if (!chainConnectors.evm) throw new Error(`This module requires an evm chain connector`);

      // TODO remove this log
      log.debug("fetchBalances", "evm-erc20", addressesByToken);
      const evmNetworks = await chaindataProvider.evmNetworksById();
      const tokens = await chaindataProvider.tokensById();
      return fetchBalances$2(chainConnectors.evm, evmNetworks, tokens, addressesByToken);
    }
  };
};
const fetchBalances$2 = async (evmChainConnector, evmNetworks, tokens, addressesByToken) => {
  const addressesByTokenGroupedByEvmNetwork = groupAddressesByTokenByEvmNetwork(addressesByToken, tokens);
  const balances = (await Promise.allSettled(Object.entries(addressesByTokenGroupedByEvmNetwork).map(async ([evmNetworkId, addressesByToken]) => {
    if (!evmChainConnector) throw new Error(`This module requires an evm chain connector`);
    const evmNetwork = evmNetworks[evmNetworkId];
    if (!evmNetwork) throw new Error(`Evm network ${evmNetworkId} not found`);
    const publicClient = await evmChainConnector.getPublicClientForEvmNetwork(evmNetworkId);
    if (!publicClient) throw new Error(`Could not get rpc provider for evm network ${evmNetworkId}`);
    const tokensAndAddresses = Object.entries(addressesByToken).reduce((tokensAndAddresses, [tokenId, addresses]) => {
      const token = tokens[tokenId];
      if (!token) {
        log.debug(`Token ${tokenId} not found`);
        return tokensAndAddresses;
      }

      // TODO: Fix @talismn/balances-react: it shouldn't pass every token to every module
      if (token.type !== "evm-erc20") {
        log.debug(`This module doesn't handle tokens of type ${token.type}`);
        return tokensAndAddresses;
      }
      const tokenAndAddresses = [token, addresses];
      return [...tokensAndAddresses, tokenAndAddresses];
    }, []);

    // fetch all balances
    const balanceRequests = tokensAndAddresses.flatMap(([token, addresses]) => {
      return addresses.map(async address => new Balance({
        source: "evm-erc20",
        status: "live",
        address: address,
        multiChainId: {
          evmChainId: evmNetwork.id
        },
        evmNetworkId,
        tokenId: token.id,
        free: await getFreeBalance$1(publicClient, token.contractAddress, address)
      }));
    });

    // wait for balance fetches to complete
    const balanceResults = await Promise.allSettled(balanceRequests);

    // filter out errors
    const balances = balanceResults.map(result => {
      if (result.status === "rejected") {
        log.debug(result.reason);
        return false;
      }
      return result.value;
    }).filter(balance => balance !== false);

    // return to caller
    return new Balances(balances);
  }))).map(result => {
    if (result.status === "rejected") {
      log.debug(result.reason);
      return false;
    }
    return result.value;
  }).filter(balances => balances !== false);
  return balances.reduce((allBalances, balances) => allBalances.add(balances), new Balances([]));
};
function groupAddressesByTokenByEvmNetwork(addressesByToken, tokens) {
  return Object.entries(addressesByToken).reduce((byChain, [tokenId, addresses]) => {
    const token = tokens[tokenId];
    if (!token) {
      log.error(`Token ${tokenId} not found`);
      return byChain;
    }
    const chainId = token.evmNetwork?.id;
    if (!chainId) {
      log.error(`Token ${tokenId} has no evm network`);
      return byChain;
    }
    if (!byChain[chainId]) byChain[chainId] = {};
    byChain[chainId][tokenId] = addresses;
    return byChain;
  }, {});
}
async function getFreeBalance$1(publicClient, contractAddress, accountAddress) {
  if (!isEthereumAddress(accountAddress)) return "0";
  try {
    const res = await publicClient.readContract({
      abi: erc20Abi,
      address: contractAddress,
      functionName: "balanceOf",
      args: [accountAddress]
    });
    return res.toString();
  } catch (error) {
    const errorMessage = hasOwnProperty(error, "shortMessage") ? error.shortMessage : hasOwnProperty(error, "message") ? error.message : error;
    log.warn(`Failed to get balance from contract ${contractAddress} (chain ${publicClient.chain?.id}) for address ${accountAddress}: ${errorMessage}`);
    throw new Error(`Failed to get balance from contract ${contractAddress} (chain ${publicClient.chain?.id}) for address ${accountAddress}`, {
      cause: error
    });
  }
}

const abiMulticall = parseAbi(["struct Call { address target; bytes callData; }", "struct Call3 { address target; bool allowFailure; bytes callData; }", "struct Call3Value { address target; bool allowFailure; uint256 value; bytes callData; }", "struct Result { bool success; bytes returnData; }", "function aggregate(Call[] calldata calls) public payable returns (uint256 blockNumber, bytes[] memory returnData)", "function aggregate3(Call3[] calldata calls) public payable returns (Result[] memory returnData)", "function aggregate3Value(Call3Value[] calldata calls) public payable returns (Result[] memory returnData)", "function blockAndAggregate(Call[] calldata calls) public payable returns (uint256 blockNumber, bytes32 blockHash, Result[] memory returnData)", "function getBasefee() view returns (uint256 basefee)", "function getBlockHash(uint256 blockNumber) view returns (bytes32 blockHash)", "function getBlockNumber() view returns (uint256 blockNumber)", "function getChainId() view returns (uint256 chainid)", "function getCurrentBlockCoinbase() view returns (address coinbase)", "function getCurrentBlockDifficulty() view returns (uint256 difficulty)", "function getCurrentBlockGasLimit() view returns (uint256 gaslimit)", "function getCurrentBlockTimestamp() view returns (uint256 timestamp)", "function getEthBalance(address addr) view returns (uint256 balance)", "function getLastBlockHash() view returns (bytes32 blockHash)", "function tryAggregate(bool requireSuccess, Call[] calldata calls) public payable returns (Result[] memory returnData)", "function tryBlockAndAggregate(bool requireSuccess, Call[] calldata calls) public payable returns (uint256 blockNumber, bytes32 blockHash, Result[] memory returnData)"]);

const evmNativeTokenId = chainId => `${chainId}-evm-native`.toLowerCase().replace(/ /g, "-");
const getEvmNetworkIdFromTokenId = tokenId => {
  const evmNetworkId = tokenId.split("-")[0];
  if (!evmNetworkId) throw new Error(`Can't detect chainId for token ${tokenId}`);
  return evmNetworkId;
};
const EvmNativeModule = hydrate => {
  const {
    chainConnectors,
    chaindataProvider
  } = hydrate;
  return {
    ...DefaultBalanceModule("evm-native"),
    /**
     * This method is currently executed on [a squid](https://github.com/TalismanSociety/chaindata-squid/blob/0ee02818bf5caa7362e3f3664e55ef05ec8df078/src/steps/updateEvmNetworksFromGithub.ts#L280-L284).
     * In a future version of the balance libraries, we may build some kind of async scheduling system which will keep the chainmeta for each chain up to date without relying on a squid.
     */
    async fetchEvmChainMeta(chainId) {
      const isTestnet = (await chaindataProvider.evmNetworkById(chainId))?.isTestnet || false;
      return {
        isTestnet
      };
    },
    /**
     * This method is currently executed on [a squid](https://github.com/TalismanSociety/chaindata-squid/blob/0ee02818bf5caa7362e3f3664e55ef05ec8df078/src/steps/updateEvmNetworksFromGithub.ts#L338-L343).
     * In a future version of the balance libraries, we may build some kind of async scheduling system which will keep the list of tokens for each chain up to date without relying on a squid.
     */
    async fetchEvmChainTokens(chainId, chainMeta, moduleConfig) {
      const {
        isTestnet
      } = chainMeta;
      const symbol = moduleConfig?.symbol ?? "ETH";
      const decimals = typeof moduleConfig?.decimals === "number" ? moduleConfig.decimals : 18;
      const id = evmNativeTokenId(chainId);
      const nativeToken = {
        id,
        type: "evm-native",
        isTestnet,
        isDefault: true,
        symbol,
        decimals,
        logo: moduleConfig?.logo || githubTokenLogoUrl(id),
        evmNetwork: {
          id: chainId
        }
      };
      if (moduleConfig?.symbol) nativeToken.symbol = moduleConfig?.symbol;
      if (moduleConfig?.coingeckoId) nativeToken.coingeckoId = moduleConfig?.coingeckoId;
      if (moduleConfig?.dcentName) nativeToken.dcentName = moduleConfig?.dcentName;
      if (moduleConfig?.mirrorOf) nativeToken.mirrorOf = moduleConfig?.mirrorOf;
      return {
        [nativeToken.id]: nativeToken
      };
    },
    getPlaceholderBalance(tokenId, address) {
      const evmNetworkId = getEvmNetworkIdFromTokenId(tokenId);
      return {
        source: "evm-native",
        status: "initializing",
        address: address,
        multiChainId: {
          evmChainId: evmNetworkId
        },
        evmNetworkId,
        tokenId,
        free: "0"
      };
    },
    async subscribeBalances(addressesByToken, callback) {
      // TODO remove
      log.debug("subscribeBalances", "evm-native", addressesByToken);
      let subscriptionActive = true;
      const subscriptionInterval = 6_000; // 6_000ms == 6 seconds
      const initDelay = 500; // 500ms == 0.5 seconds
      const cache = new Map();

      // for chains with a zero balance we only call fetchBalances once every 5 subscriptionIntervals
      // if subscriptionInterval is 6 seconds, this means we only poll chains with a zero balance every 30 seconds
      let zeroBalanceSubscriptionIntervalCounter = 0;
      const evmNetworks = await chaindataProvider.evmNetworksById();
      const tokens = await chaindataProvider.tokensById();
      const poll = async () => {
        if (!subscriptionActive) return;
        zeroBalanceSubscriptionIntervalCounter = (zeroBalanceSubscriptionIntervalCounter + 1) % 5;
        try {
          // fetch balance for each network sequentially to prevent creating a big queue of http requests (browser can only handle 2 at a time)
          // since these are native tokens (1 per network), we can iterate on tokens
          for (const tokenId of Object.keys(addressesByToken)) {
            const cached = cache.get(tokenId);
            if (cached && zeroBalanceSubscriptionIntervalCounter !== 0 && new Balances(cached).each.reduce((sum, b) => sum + b.total.planck, 0n) === 0n) {
              // only poll empty token balances every 5 subscriptionIntervals
              continue;
            }
            try {
              const tokenAddresses = {
                [tokenId]: addressesByToken[tokenId]
              };
              if (!chainConnectors.evm) throw new Error(`This module requires an evm chain connector`);
              const balances = await fetchBalances$1(chainConnectors.evm, evmNetworks, tokens, tokenAddresses);

              // Don't call callback with balances which have not changed since the last poll.
              const json = balances.toJSON();
              if (!isEqual(cache.get(tokenId), json)) {
                cache.set(tokenId, json);
                callback(null, balances);
              }
            } catch (err) {
              callback(err);
            }
          }
        } catch (error) {
          callback(error);
        } finally {
          setTimeout(poll, subscriptionInterval);
        }
      };
      setTimeout(poll, initDelay);
      return () => {
        subscriptionActive = false;
      };
    },
    async fetchBalances(addressesByToken) {
      if (!chainConnectors.evm) throw new Error(`This module requires an evm chain connector`);
      const evmNetworks = await chaindataProvider.evmNetworksById();
      const tokens = await chaindataProvider.tokensById();
      return fetchBalances$1(chainConnectors.evm, evmNetworks, tokens, addressesByToken);
    }
  };
};
const fetchBalances$1 = async (evmChainConnector, evmNetworks, tokens, addressesByToken) => {
  const balances = (await Promise.allSettled(Object.entries(addressesByToken).map(async ([tokenId, addresses]) => {
    if (!evmChainConnector) throw new Error(`This module requires an evm chain connector`);
    const token = tokens[tokenId];
    if (!token) throw new Error(`Token ${tokenId} not found`);

    // TODO: Fix @talismn/balances-react: it shouldn't pass every token to every module
    if (token.type !== "evm-native") throw new Error(`This module doesn't handle tokens of type ${token.type}`);
    const evmNetworkId = token.evmNetwork?.id;
    if (!evmNetworkId) throw new Error(`Token ${tokenId} has no evm network`);
    const evmNetwork = evmNetworks[evmNetworkId];
    if (!evmNetwork) throw new Error(`Evm network ${evmNetworkId} not found`);
    const publicClient = await evmChainConnector.getPublicClientForEvmNetwork(evmNetworkId);
    if (!publicClient) throw new Error(`Could not get rpc provider for evm network ${evmNetworkId}`);

    // fetch all balances
    const freeBalances = await getFreeBalances(publicClient, addresses);
    const balanceResults = addresses.map((address, i) => {
      if (freeBalances[i] === "error") return false;
      return new Balance({
        source: "evm-native",
        status: "live",
        address: address,
        multiChainId: {
          evmChainId: evmNetwork.id
        },
        evmNetworkId,
        tokenId,
        free: freeBalances[i].toString()
      });
    }).filter(balance => balance !== false);

    // return to caller
    return new Balances(balanceResults);
  }))).map(result => {
    if (result.status === "rejected") {
      log.debug(result.reason);
      return false;
    }
    return result.value;
  }).filter(balances => balances !== false);
  return balances.reduce((allBalances, balances) => allBalances.add(balances), new Balances([]));
};
async function getFreeBalance(publicClient, address) {
  if (!isEthereumAddress(address)) return 0n;
  try {
    return await publicClient.getBalance({
      address
    });
  } catch (error) {
    const errorMessage = hasOwnProperty(error, "shortMessage") ? error.shortMessage : hasOwnProperty(error, "message") ? error.message : error;
    log.warn(`Failed to get balance from chain ${publicClient.chain?.id} for address ${address}: ${errorMessage}`);
    return "error";
  }
}
async function getFreeBalances(publicClient, addresses) {
  // if multicall is available, use it to save RPC rate limits
  if (publicClient.batch?.multicall && publicClient.chain?.contracts?.multicall3?.address) {
    try {
      const ethAddresses = addresses.filter(isEthereumAddress);
      const addressMulticall = publicClient.chain.contracts.multicall3.address;
      const callResults = await publicClient.multicall({
        contracts: ethAddresses.map(address => ({
          address: addressMulticall,
          abi: abiMulticall,
          functionName: "getEthBalance",
          args: [address]
        }))
      });
      const ethBalanceResults = Object.fromEntries(ethAddresses.map((address, i) => {
        const {
          error
        } = callResults[i];
        if (error) {
          const errorMessage = hasOwnProperty(error, "shortMessage") ? error.shortMessage : hasOwnProperty(error, "message") ? error.message : error;
          log.warn(`Failed to get balance from chain ${publicClient.chain?.id} for address ${address}: ${errorMessage}`);
        }
        return [address, callResults[i].result ?? "error"];
      }));

      // default to 0 for non evm addresses
      return addresses.map(address => {
        const val = ethBalanceResults[address];
        if (isHex(val)) return hexToBigInt(val);
        return val ?? 0n;
      });
    } catch (err) {
      const errorMessage = hasOwnProperty(err, "shortMessage") ? err.shortMessage : hasOwnProperty(err, "message") ? err.message : err;
      log.warn(`Failed to get balance from chain ${publicClient.chain?.id} for ${addresses.length} addresses: ${errorMessage}`);
      return addresses.map(() => "error");
    }
  }
  return Promise.all(addresses.map(address => getFreeBalance(publicClient, address)));
}

/**
 * Wraps a BalanceModule's fetch/subscribe methods with a single `balances` method.
 * This `balances` method will subscribe if a callback parameter is provided, or otherwise fetch.
 */

async function balances(balanceModule, addressesByToken, callback) {
  // subscription request
  if (callback !== undefined) return await balanceModule.subscribeBalances(addressesByToken, callback);

  // one-off request
  return await balanceModule.fetchBalances(addressesByToken);
}
const createTypeRegistryCache = () => {
  const typeRegistryCache = new Map();
  const getOrCreateTypeRegistry = (chainId, metadataRpc) => {
    const cached = typeRegistryCache.get(chainId);
    if (cached && cached.metadataRpc === metadataRpc) return cached.typeRegistry;
    const typeRegistry = new TypeRegistry();
    if (typeof metadataRpc === "string") {
      try {
        const metadata = new Metadata(typeRegistry, metadataRpc);
        metadata.registry.setMetadata(metadata);
      } catch (cause) {
        log.warn(new Error(`Failed to set metadata for chain ${chainId}`, {
          cause
        }), cause);
      }
    }
    typeRegistryCache.set(chainId, {
      metadataRpc,
      typeRegistry
    });
    return typeRegistry;
  };
  return {
    getOrCreateTypeRegistry
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any

// eslint-disable-next-line @typescript-eslint/no-explicit-any

/**
 * The following `Infer*` collection of generic types can be used when you want to
 * extract one of the generic type arguments from an existing BalanceModule.
 *
 * For example, you might want to write a function which can accept any BalanceModule
 * as an input, and then return the specific TokenType for that module:
 * function getTokens<T extends AnyBalanceModule>(module: T): InferTokenType<T>
 *
 * Or for another example, you might want a function which can take any BalanceModule `type`
 * string as input, and then return some data associated with that module with the correct type:
 * function getChainMeta<T extends AnyBalanceModule>(type: InferModuleType<T>): InferChainMeta<T> | undefined
 */

/**
 * Given a `moduleType` and a `chain` from a chaindataProvider, this function will find the chainMeta
 * associated with the given balanceModule for the given chain.
 */
const findChainMeta = (miniMetadatas, moduleType, chain) => {
  if (!chain) return [undefined, undefined];
  if (!chain.specName) return [undefined, undefined];
  if (!chain.specVersion) return [undefined, undefined];

  // TODO: This is spaghetti to import this here, it should be injected into each balance module or something.
  const metadataId = deriveMiniMetadataId({
    source: moduleType,
    chainId: chain.id,
    specName: chain.specName,
    specVersion: chain.specVersion,
    balancesConfig: JSON.stringify(chain.balancesConfig?.find(config => config.moduleType === moduleType)?.moduleConfig ?? {})
  });

  // TODO: Fix this (needs to fetch miniMetadata without being async)
  const miniMetadata = miniMetadatas.get(metadataId);
  const chainMeta = miniMetadata ? {
    miniMetadata: miniMetadata.data,
    metadataVersion: miniMetadata.version,
    ...JSON.parse(miniMetadata.extra)
  } : undefined;
  return [chainMeta, miniMetadata];
};
const getValidSubscriptionIds = () => {
  return new Set(localStorage.getItem("TalismanBalancesSubscriptionIds")?.split?.(",")?.filter?.(Boolean) ?? []);
};
const createSubscriptionId = () => {
  // delete current id (if exists)
  deleteSubscriptionId();

  // create new id
  const subscriptionId = Date.now().toString();
  sessionStorage.setItem("TalismanBalancesSubscriptionId", subscriptionId);

  // add to list of current ids
  const subscriptionIds = getValidSubscriptionIds();
  subscriptionIds.add(subscriptionId);
  localStorage.setItem("TalismanBalancesSubscriptionIds", [...subscriptionIds].filter(Boolean).filter(storageId =>
  // filter super old IDs (they tend to stick around when the background script is restarted)
  //
  // test if the difference between `now` and `then` (subscriptionId - storageId) is greater than 1 week in milliseconds (604_800_000)
  // if so, `storageId` is definitely super old and we can just prune it from localStorage
  parseInt(subscriptionId, 10) - parseInt(storageId, 10) >= 604_800_000 ? false : true).join(","));
  return subscriptionId;
};
const deleteSubscriptionId = () => {
  const subscriptionId = sessionStorage.getItem("TalismanBalancesSubscriptionId");
  if (!subscriptionId) return;
  const subscriptionIds = getValidSubscriptionIds();
  subscriptionIds.delete(subscriptionId);
  localStorage.setItem("TalismanBalancesSubscriptionIds", [...subscriptionIds].filter(Boolean).join(","));
};

/**
 * Sets all balance statuses from `live-${string}` to either `live` or `cached`
 *
 * You should make sure that the input collection `balances` is mutable, because the statuses
 * will be changed in-place as a performance consideration.
 */
const deriveStatuses = (validSubscriptionIds, balances) => {
  balances.forEach(balance => {
    if (["live", "cache", "stale", "initializing"].includes(balance.status)) return balance;
    if (validSubscriptionIds.size < 1) {
      balance.status = "cache";
      return balance;
    }
    if (!validSubscriptionIds.has(balance.status.slice("live-".length))) {
      balance.status = "cache";
      return balance;
    }
    balance.status = "live";
    return balance;
  });
  return balances;
};

/**
 * Used by a variety of balance modules to help encode and decode substrate state calls.
 */
class StorageHelper {
  #registry;
  #storageKey;
  #module;
  #method;
  #parameters;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tags = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(registry, module, method, ...parameters) {
    this.#registry = registry;
    this.#module = module;
    this.#method = method;
    this.#parameters = parameters;
    const _metadataVersion = 0; // is not used inside the decorateStorage function
    let query;
    try {
      query = decorateStorage(registry, registry.metadata, _metadataVersion);
    } catch (error) {
      log.debug(`Failed to decorate storage: ${error.message}`);
      this.#storageKey = null;
    }
    try {
      if (!query) throw new Error(`decoratedStorage unavailable`);
      this.#storageKey = new StorageKey(registry, parameters ? [query[module][method], parameters] : query[module][method]);
    } catch (error) {
      log.debug(`Failed to create storageKey ${module || "unknown"}.${method || "unknown"}: ${error.message}`);
      this.#storageKey = null;
    }
  }
  get stateKey() {
    return this.#storageKey?.toHex();
  }
  get module() {
    return this.#module;
  }
  get method() {
    return this.#method;
  }
  get parameters() {
    return this.#parameters;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tag(tags) {
    this.tags = tags;
    return this;
  }
  decode(input) {
    if (!this.#storageKey) return;
    return this.#decodeStorageScaleResponse(this.#registry, this.#storageKey, input);
  }
  #decodeStorageScaleResponse(typeRegistry, storageKey, input) {
    if (input === undefined || input === null) return;
    const type = storageKey.outputType || "Raw";
    const meta = storageKey.meta || {
      fallback: undefined,
      modifier: {
        isOptional: true
      },
      type: {
        asMap: {
          linked: {
            isTrue: false
          }
        },
        isMap: false
      }
    };
    try {
      return typeRegistry.createTypeUnsafe(type, [meta.modifier.isOptional ? typeRegistry.createTypeUnsafe(type, [input], {
        isPedantic: true
      }) : input], {
        isOptional: meta.modifier.isOptional,
        isPedantic: !meta.modifier.isOptional
      });
    } catch (error) {
      throw new Error(`Unable to decode storage ${storageKey.section || "unknown"}.${storageKey.method || "unknown"}: ${error.message}`);
    }
  }
}

/**
 * Pass some these into an `RpcStateQueryHelper` in order to easily batch multiple state queries into the one rpc call.
 */

/**
 * Used by a variety of balance modules to help batch multiple state queries into the one rpc call.
 */
class RpcStateQueryHelper {
  #chainConnector;
  #queries;
  constructor(chainConnector, queries) {
    this.#chainConnector = chainConnector;
    this.#queries = queries;
  }
  async subscribe(callback, timeout = false, subscribeMethod = "state_subscribeStorage", responseMethod = "state_storage", unsubscribeMethod = "state_unsubscribeStorage") {
    const queriesByChain = groupBy(this.#queries, "chainId");
    const subscriptions = Object.entries(queriesByChain).map(([chainId, queries]) => {
      const params = [queries.map(({
        stateKey
      }) => stateKey)];
      const unsub = this.#chainConnector.subscribe(chainId, subscribeMethod, responseMethod, params, (error, result) => {
        error ? callback(error) : callback(null, this.#distributeChangesToQueryDecoders.call(this, chainId, result));
      }, timeout);
      return () => unsub.then(unsubscribe => unsubscribe(unsubscribeMethod));
    });
    return () => subscriptions.forEach(unsubscribe => unsubscribe());
  }
  async fetch(method = "state_queryStorageAt") {
    const queriesByChain = groupBy(this.#queries, "chainId");
    const resultsByChain = await Promise.all(Object.entries(queriesByChain).map(async ([chainId, queries]) => {
      const params = [queries.map(({
        stateKey
      }) => stateKey)];
      const result = (await this.#chainConnector.send(chainId, method, params))[0];
      return this.#distributeChangesToQueryDecoders.call(this, chainId, result);
    }));
    return resultsByChain.flatMap(result => result);
  }
  #distributeChangesToQueryDecoders(chainId, result) {
    if (typeof result !== "object" || result === null) return [];
    if (!hasOwnProperty(result, "changes") || typeof result.changes !== "object") return [];
    if (!Array.isArray(result.changes)) return [];
    return result.changes.flatMap(([reference, change]) => {
      if (typeof reference !== "string") {
        log.warn(`Received non-string reference in RPC result: ${reference}`);
        return [];
      }
      if (typeof change !== "string" && change !== null) {
        log.warn(`Received non-string and non-null change in RPC result: ${reference} | ${change}`);
        return [];
      }
      const query = this.#queries.find(({
        chainId: cId,
        stateKey
      }) => cId === chainId && stateKey === reference);
      if (!query) {
        log.warn(`Failed to find query:\n${reference} in\n${this.#queries.map(({
          stateKey
        }) => stateKey)}`);
        return [];
      }
      return [query.decodeResult(change)];
    });
  }
}
const subshapeStorageDecoder =
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(miniMetadata, module, method) => {
  if (miniMetadata.version !== 14) {
    log.warn(`Cannot decode metadata version ${miniMetadata.version} (${miniMetadata.chainId} ${miniMetadata.source})`);
    return;
  }
  module = camelCase(module);
  method = camelCase(method);
  if (miniMetadata.data === null) return;
  const metadata = $metadataV14.decode($.decodeHex(miniMetadata.data));
  const typeId = metadata.pallets.find(pallet => camelCase(pallet.name) === module)?.storage?.entries?.find(entry => camelCase(entry.name) === method)?.value;
  if (!typeId) {
    log.warn(`Type definition not found in metadata for ${module}::${method} (${miniMetadata.chainId} ${miniMetadata.source})`);
    return;
  }
  return getShape(metadata, typeId);
};
const getUniqueChainIds = (addressesByToken, tokens) => [...new Set(Object.keys(addressesByToken).map(tokenId => tokens[tokenId]?.chain?.id).flatMap(chainId => chainId ? [chainId] : []))];
const buildStorageDecoders = ({
  chainIds,
  chains,
  miniMetadatas,
  moduleType,
  decoders
}) => new Map([...chainIds].flatMap(chainId => {
  const chain = chains[chainId];
  if (!chain) return [];
  const [, miniMetadata] = findChainMeta(miniMetadatas, moduleType, chain);
  if (!miniMetadata) return [];
  if (miniMetadata.version < 14) return [];
  const builtDecoders = Object.fromEntries(Object.entries(decoders).map(([key, [module, method]]) => [key, subshapeStorageDecoder(miniMetadata, module, method)]));
  return [[chainId, builtDecoders]];
}));

/**
 *
 * Detect Balances::transfer -> Balances::transfer_allow_death migration
 * https://github.com/paritytech/substrate/pull/12951
 *
 * `transfer_allow_death` is the preferred method,
 * so if something goes wrong during detection, we should assume the chain has migrated
 * @param metadataRpc string containing the hashed RPC metadata for the chain
 * @returns
 */
const detectTransferMethod = metadataRpc => {
  const pjsMetadata = new Metadata(new TypeRegistry(), metadataRpc);
  pjsMetadata.registry.setMetadata(pjsMetadata);
  const balancesPallet = pjsMetadata.asLatest.pallets.find(pallet => pallet.name.eq("Balances"));
  const balancesCallsTypeIndex = balancesPallet?.calls.value.type.toNumber();
  const balancesCallsType = balancesCallsTypeIndex !== undefined ? pjsMetadata.asLatest.lookup.types[balancesCallsTypeIndex] : undefined;
  const hasDeprecatedTransferCall = balancesCallsType?.type.def.asVariant?.variants.find(variant => variant.name.eq("transfer")) !== undefined;
  return hasDeprecatedTransferCall ? "transfer" : "transferAllowDeath";
};

const subAssetTokenId = (chainId, assetId, tokenSymbol) => `${chainId}-substrate-assets-${assetId}-${tokenSymbol}`.toLowerCase().replace(/ /g, "-");
const getChainIdFromTokenId$4 = tokenId => {
  const match = /^([\d\w-]+)-substrate-assets/.exec(tokenId);
  if (!match?.[1]) throw new Error(`Can't detect chainId for token ${tokenId}`);
  return match?.[1];
};
const SubAssetsModule = hydrate => {
  const {
    chainConnectors,
    chaindataProvider
  } = hydrate;
  const chainConnector = chainConnectors.substrate;
  assert(chainConnector, "This module requires a substrate chain connector");
  const {
    getOrCreateTypeRegistry
  } = createTypeRegistryCache();
  return {
    ...DefaultBalanceModule("substrate-assets"),
    async fetchSubstrateChainMeta(chainId, moduleConfig, metadataRpc) {
      const isTestnet = (await chaindataProvider.chainById(chainId))?.isTestnet || false;
      if (metadataRpc === undefined) return {
        isTestnet,
        miniMetadata: null,
        metadataVersion: 0
      };
      const metadataVersion = getMetadataVersion(metadataRpc);
      if ((moduleConfig?.tokens ?? []).length < 1) return {
        isTestnet,
        miniMetadata: null,
        metadataVersion
      };
      if (metadataVersion !== 14) return {
        isTestnet,
        miniMetadata: null,
        metadataVersion
      };
      const metadata = $metadataV14.decode($.decodeHex(metadataRpc));
      const isAssetsPallet = pallet => pallet.name === "Assets";
      const isAccountItem = item => item.name === "Account";
      const isAssetItem = item => item.name === "Asset";
      const isMetadataItem = item => item.name === "Metadata";

      // TODO: Handle metadata v15
      filterMetadataPalletsAndItems(metadata, [{
        pallet: isAssetsPallet,
        items: [isAccountItem, isAssetItem, isMetadataItem]
      }]);
      metadata.extrinsic.signedExtensions = [];
      const miniMetadata = $.encodeHexPrefixed($metadataV14.encode(metadata));
      return {
        isTestnet,
        miniMetadata,
        metadataVersion
      };
    },
    async fetchSubstrateChainTokens(chainId, chainMeta, moduleConfig) {
      const {
        isTestnet,
        miniMetadata: metadataRpc,
        metadataVersion
      } = chainMeta;
      if ((moduleConfig?.tokens ?? []).length < 1) return {};
      const registry = new TypeRegistry();
      if (metadataRpc !== null && metadataVersion >= 14) registry.setMetadata(new Metadata(registry, metadataRpc));
      const tokens = {};
      for (const tokenConfig of moduleConfig?.tokens ?? []) {
        try {
          const assetId = new BN(tokenConfig.assetId);
          const assetQuery = new StorageHelper(registry, "assets", "asset", assetId);
          const metadataQuery = new StorageHelper(registry, "assets", "metadata", assetId);
          const [
          // e.g.
          // Option<{
          //   owner: HKKT5DjFaUE339m7ZWS2yutjecbUpBcDQZHw2EF7SFqSFJH
          //   issuer: HKKT5DjFaUE339m7ZWS2yutjecbUpBcDQZHw2EF7SFqSFJH
          //   admin: HKKT5DjFaUE339m7ZWS2yutjecbUpBcDQZHw2EF7SFqSFJH
          //   freezer: HKKT5DjFaUE339m7ZWS2yutjecbUpBcDQZHw2EF7SFqSFJH
          //   supply: 99,996,117,733,044,042
          //   deposit: 1,000,000,000,000
          //   minBalance: 100,000
          //   isSufficient: true
          //   accounts: 6,032
          //   sufficients: 1,542
          //   approvals: 1
          //   status: Live
          // }>
          assetsAsset,
          // e.g.
          // {
          //   deposit: 6,693,333,000
          //   name: RMRK.app
          //   symbol: RMRK
          //   decimals: 10
          //   isFrozen: false
          // }
          assetsMetadata] = await Promise.all([chainConnector.send(chainId, "state_getStorage", [assetQuery.stateKey]).then(result => assetQuery.decode(result)), chainConnector.send(chainId, "state_getStorage", [metadataQuery.stateKey]).then(result => metadataQuery.decode(result))]);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const unsafeAssetsMetadata = assetsMetadata;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const unsafeAssetsAsset = assetsAsset;
          const existentialDeposit = unsafeAssetsAsset?.value?.minBalance?.toBigInt?.()?.toString?.() ?? "0";
          const symbol = unsafeAssetsMetadata?.symbol?.toHuman?.() ?? "Unit";
          const decimals = unsafeAssetsMetadata?.decimals?.toNumber?.() ?? 0;
          const isFrozen = unsafeAssetsMetadata?.isFrozen?.toHuman?.() ?? false;
          const id = subAssetTokenId(chainId, assetId.toString(10), symbol);
          const token = {
            id,
            type: "substrate-assets",
            isTestnet,
            isDefault: tokenConfig?.isDefault ?? true,
            symbol,
            decimals,
            logo: tokenConfig?.logo || githubTokenLogoUrl(id),
            existentialDeposit,
            assetId: assetId.toString(10),
            isFrozen,
            chain: {
              id: chainId
            }
          };
          if (tokenConfig?.symbol) token.symbol = tokenConfig?.symbol;
          if (tokenConfig?.coingeckoId) token.coingeckoId = tokenConfig?.coingeckoId;
          if (tokenConfig?.dcentName) token.dcentName = tokenConfig?.dcentName;
          if (tokenConfig?.mirrorOf) token.mirrorOf = tokenConfig?.mirrorOf;
          tokens[token.id] = token;
        } catch (error) {
          log.error(`Failed to build substrate-assets token ${tokenConfig.assetId} (${tokenConfig.symbol}) on ${chainId}`, error);
          continue;
        }
      }
      return tokens;
    },
    getPlaceholderBalance(tokenId, address) {
      const chainId = getChainIdFromTokenId$4(tokenId);
      return {
        source: "substrate-assets",
        status: "initializing",
        address,
        multiChainId: {
          subChainId: chainId
        },
        chainId,
        tokenId,
        free: "0",
        locks: "0"
      };
    },
    // TODO: Don't create empty subscriptions
    async subscribeBalances(addressesByToken, callback) {
      const queries = await buildQueries$3(chaindataProvider, getOrCreateTypeRegistry, addressesByToken);
      const unsubscribe = await new RpcStateQueryHelper(chainConnector, queries).subscribe((error, result) => error ? callback(error) : callback(null, new Balances(result ?? [])));
      return unsubscribe;
    },
    async fetchBalances(addressesByToken) {
      assert(chainConnectors.substrate, "This module requires a substrate chain connector");
      const queries = await buildQueries$3(chaindataProvider, getOrCreateTypeRegistry, addressesByToken);
      const result = await new RpcStateQueryHelper(chainConnectors.substrate, queries).fetch();
      return new Balances(result ?? []);
    },
    async transferToken({
      tokenId,
      from,
      to,
      amount,
      registry,
      metadataRpc,
      blockHash,
      blockNumber,
      nonce,
      specVersion,
      transactionVersion,
      tip,
      transferMethod
    }) {
      const token = await chaindataProvider.tokenById(tokenId);
      assert(token, `Token ${tokenId} not found in store`);
      if (token.type !== "substrate-assets") throw new Error(`This module doesn't handle tokens of type ${token.type}`);
      const chainId = token.chain.id;
      const chain = await chaindataProvider.chainById(chainId);
      assert(chain?.genesisHash, `Chain ${chainId} not found in store`);
      const {
        genesisHash
      } = chain;
      const id = token.assetId;
      const pallet = "assets";
      const method =
      // the assets pallet has no transferAll method
      transferMethod === "transferAll" ? "transfer" : transferMethod;
      const args = {
        id,
        target: {
          Id: to
        },
        amount
      };
      const unsigned = defineMethod({
        method: {
          pallet,
          name: method,
          args
        },
        address: from,
        blockHash,
        blockNumber,
        eraPeriod: 64,
        genesisHash,
        metadataRpc,
        nonce,
        specVersion,
        tip: tip ? Number(tip) : 0,
        transactionVersion
      }, {
        metadataRpc,
        registry
      });
      return {
        type: "substrate",
        tx: unsigned
      };
    }
  };
};
async function buildQueries$3(chaindataProvider, getOrCreateTypeRegistry, addressesByToken) {
  const chains = await chaindataProvider.chainsById();
  const tokens = await chaindataProvider.tokensById();
  const miniMetadatas = new Map((await db.miniMetadatas.toArray()).map(miniMetadata => [miniMetadata.id, miniMetadata]));
  const uniqueChainIds = getUniqueChainIds(addressesByToken, tokens);
  const chainStorageDecoders = buildStorageDecoders({
    chainIds: uniqueChainIds,
    chains,
    miniMetadatas,
    moduleType: "substrate-assets",
    decoders: {
      storageDecoder: ["assets", "account"]
    }
  });
  return Object.entries(addressesByToken).flatMap(([tokenId, addresses]) => {
    const token = tokens[tokenId];
    if (!token) {
      log.warn(`Token ${tokenId} not found`);
      return [];
    }
    if (token.type !== "substrate-assets") {
      log.debug(`This module doesn't handle tokens of type ${token.type}`);
      return [];
    }
    const chainId = token.chain?.id;
    if (!chainId) {
      log.warn(`Token ${tokenId} has no chain`);
      return [];
    }
    const chain = chains[chainId];
    if (!chain) {
      log.warn(`Chain ${chainId} for token ${tokenId} not found`);
      return [];
    }
    const [chainMeta] = findChainMeta(miniMetadatas, "substrate-assets", chain);
    const registry = chainMeta?.miniMetadata !== undefined && chainMeta?.miniMetadata !== null && chainMeta?.metadataVersion >= 14 ? getOrCreateTypeRegistry(chainId, chainMeta.miniMetadata) : new TypeRegistry();
    return addresses.flatMap(address => {
      const storageHelper = new StorageHelper(registry, "assets", "account", token.assetId, decodeAnyAddress(address));
      const storageDecoder = chainStorageDecoders.get(chainId)?.storageDecoder;
      const stateKey = storageHelper.stateKey;
      if (!stateKey) return [];
      const decodeResult = change => {
        // e.g.
        // Option<{
        //   balance: 2,000,000,000
        //   isFrozen: false
        //   reason: Sufficient
        //   extra: null
        // }>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const balance = (storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) :
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        null) ?? {
          balance: 0n,
          status: "Liquid"
        };
        const isFrozen = balance?.status === "Frozen";
        const amount = (balance?.balance ?? 0n).toString();

        // due to the following balance calculations, which are made in the `Balance` type:
        //
        // total balance        = (free balance) + (reserved balance)
        // transferable balance = (free balance) - (frozen balance)
        //
        // when `isFrozen` is true we need to set **both** the `free` and `frozen` amounts
        // of this balance to the value we received from the RPC.
        //
        // if we only set the `frozen` amount, then the `total` calculation will be incorrect!
        const free = amount;
        const frozen = token.isFrozen || isFrozen ? amount : "0";
        return new Balance({
          source: "substrate-assets",
          status: "live",
          address,
          multiChainId: {
            subChainId: chainId
          },
          chainId,
          tokenId: token.id,
          free,
          locks: frozen
        });
      };
      return {
        chainId,
        stateKey,
        decodeResult
      };
    });
  });
}

const subEquilibriumTokenId = (chainId, tokenSymbol) => `${chainId}-substrate-equilibrium-${tokenSymbol}`.toLowerCase().replace(/ /g, "-");
const getChainIdFromTokenId$3 = tokenId => {
  const match = /^([\d\w-]+)-substrate-equilibrium/.exec(tokenId);
  if (!match?.[1]) throw new Error(`Can't detect chainId for token ${tokenId}`);
  return match?.[1];
};
const SubEquilibriumModule = hydrate => {
  const {
    chainConnectors,
    chaindataProvider
  } = hydrate;
  const chainConnector = chainConnectors.substrate;
  assert(chainConnector, "This module requires a substrate chain connector");
  const {
    getOrCreateTypeRegistry
  } = createTypeRegistryCache();
  return {
    ...DefaultBalanceModule("substrate-equilibrium"),
    async fetchSubstrateChainMeta(chainId, moduleConfig, metadataRpc) {
      const isTestnet = (await chaindataProvider.chainById(chainId))?.isTestnet || false;
      if (metadataRpc === undefined) return {
        isTestnet,
        miniMetadata: null,
        metadataVersion: 0
      };
      const metadataVersion = getMetadataVersion(metadataRpc);
      // default to disabled
      if (moduleConfig?.disable !== false) return {
        isTestnet,
        miniMetadata: null,
        metadataVersion
      };
      if (metadataVersion !== 14) return {
        isTestnet,
        miniMetadata: null,
        metadataVersion
      };
      const metadata = $metadataV14.decode($.decodeHex(metadataRpc));
      const isEqAssetsPallet = pallet => pallet.name === "EqAssets";
      const isAssetsItem = item => item.name === "Assets";
      const isSystemPallet = pallet => pallet.name === "System";
      const isAccountItem = item => item.name === "Account";

      // TODO: Handle metadata v15
      filterMetadataPalletsAndItems(metadata, [{
        pallet: isEqAssetsPallet,
        items: [isAssetsItem]
      }, {
        pallet: isSystemPallet,
        items: [isAccountItem]
      }]);
      metadata.extrinsic.signedExtensions = [];
      const miniMetadata = $.encodeHexPrefixed($metadataV14.encode(metadata));
      return {
        isTestnet,
        miniMetadata,
        metadataVersion
      };
    },
    async fetchSubstrateChainTokens(chainId, chainMeta, moduleConfig) {
      // default to disabled
      if (moduleConfig?.disable !== false) return {};
      const {
        isTestnet,
        miniMetadata: metadataRpc,
        metadataVersion
      } = chainMeta;
      const registry = new TypeRegistry();
      if (metadataRpc !== null && metadataVersion >= 14) registry.setMetadata(new Metadata(registry, metadataRpc));
      const tokens = {};
      try {
        const assetsQuery = new StorageHelper(registry, "eqAssets", "assets");
        const assetsResult = await chainConnector.send(chainId, "state_getStorage", [assetsQuery.stateKey]).then(result => assetsQuery.decode(result))

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;
        [...(assetsResult?.value ?? [])].map(asset => {
          if (!asset) return;
          if (!asset?.id) return;
          const assetId = asset.id.toString(10);
          const symbol = tokenSymbolFromU64Id(asset.id);
          const id = subEquilibriumTokenId(chainId, symbol);
          const decimals = DEFAULT_DECIMALS;
          const tokenConfig = (moduleConfig?.tokens ?? []).find(token => token.assetId === assetId);
          const token = {
            id,
            type: "substrate-equilibrium",
            isTestnet,
            isDefault: tokenConfig?.isDefault ?? true,
            symbol,
            decimals,
            logo: tokenConfig?.logo || githubTokenLogoUrl(id),
            // TODO: Fetch the ED
            existentialDeposit: "0",
            assetId,
            chain: {
              id: chainId
            }
          };
          if (tokenConfig?.symbol) token.symbol = tokenConfig?.symbol;
          if (tokenConfig?.coingeckoId) token.coingeckoId = tokenConfig?.coingeckoId;
          if (tokenConfig?.dcentName) token.dcentName = tokenConfig?.dcentName;
          if (tokenConfig?.mirrorOf) token.mirrorOf = tokenConfig?.mirrorOf;
          tokens[token.id] = token;
        });
      } catch (error) {
        log.error(`Failed to build substrate-equilibrium tokens on ${chainId}`, error?.message ?? error);
      }
      return tokens;
    },
    getPlaceholderBalance(tokenId, address) {
      const chainId = getChainIdFromTokenId$3(tokenId);
      return {
        source: "substrate-equilibrium",
        status: "initializing",
        address,
        multiChainId: {
          subChainId: chainId
        },
        chainId,
        tokenId,
        free: "0"
      };
    },
    // TODO: Don't create empty subscriptions
    async subscribeBalances(addressesByToken, callback) {
      const queries = await buildQueries$2(chaindataProvider, getOrCreateTypeRegistry, addressesByToken);
      const unsubscribe = await new RpcStateQueryHelper(chainConnector, queries).subscribe((error, result) => error ? callback(error) : callback(null, new Balances(result?.flatMap(balances => balances) ?? [])));
      return unsubscribe;
    },
    async fetchBalances(addressesByToken) {
      assert(chainConnectors.substrate, "This module requires a substrate chain connector");
      const queries = await buildQueries$2(chaindataProvider, getOrCreateTypeRegistry, addressesByToken);
      const result = await new RpcStateQueryHelper(chainConnectors.substrate, queries).fetch();
      return new Balances(result.flatMap(balances => balances) ?? []);
    },
    async transferToken({
      tokenId,
      from,
      to,
      amount,
      registry,
      metadataRpc,
      blockHash,
      blockNumber,
      nonce,
      specVersion,
      transactionVersion,
      tip,
      transferMethod
    }) {
      const token = await chaindataProvider.tokenById(tokenId);
      assert(token, `Token ${tokenId} not found in store`);
      if (token.type !== "substrate-equilibrium") throw new Error(`This module doesn't handle tokens of type ${token.type}`);
      const chainId = token.chain.id;
      const chain = await chaindataProvider.chainById(chainId);
      assert(chain?.genesisHash, `Chain ${chainId} not found in store`);
      const {
        genesisHash
      } = chain;
      const {
        assetId
      } = token;
      const pallet = "eqBalances";
      const method = transferMethod === "transferAll" ?
      // the eqBalances pallet has no transferAll method
      "transfer" : transferMethod === "transferKeepAlive" ?
      // the eqBalances pallet has no transferKeepAlive method
      "transfer" : "transfer";
      const args = {
        asset: assetId,
        to,
        value: amount
      };
      const unsigned = defineMethod({
        method: {
          pallet,
          name: method,
          args
        },
        address: from,
        blockHash,
        blockNumber,
        eraPeriod: 64,
        genesisHash,
        metadataRpc,
        nonce,
        specVersion,
        tip: tip ? Number(tip) : 0,
        transactionVersion
      }, {
        metadataRpc,
        registry
      });
      return {
        type: "substrate",
        tx: unsigned
      };
    }
  };
};
async function buildQueries$2(chaindataProvider, getOrCreateTypeRegistry, addressesByToken) {
  const chains = await chaindataProvider.chainsById();
  const tokens = await chaindataProvider.tokensById();
  const miniMetadatas = new Map((await db.miniMetadatas.toArray()).map(miniMetadata => [miniMetadata.id, miniMetadata]));
  const uniqueChainIds = getUniqueChainIds(addressesByToken, tokens);
  const chainStorageDecoders = buildStorageDecoders({
    chainIds: uniqueChainIds,
    chains,
    miniMetadatas,
    moduleType: "substrate-equilibrium",
    decoders: {
      storageDecoder: ["system", "account"]
    }
  });

  // equilibrium returns all chain tokens for each address in the one query
  // so, we only need to make one query per address, rather than one query per token per address
  const addressesByChain = new Map();
  const tokensByAddress = new Map();
  Object.entries(addressesByToken).map(([tokenId, addresses]) => {
    const token = tokens[tokenId];
    if (!token) return log.warn(`Token ${tokenId} not found`);
    if (token.type !== "substrate-equilibrium") return log.debug(`This module doesn't handle tokens of type ${token.type}`);
    const chainId = token?.chain?.id;
    if (!chainId) return log.warn(`Token ${tokenId} has no chain`);
    const byChain = addressesByChain.get(chainId) ?? new Set();
    addresses.forEach(address => {
      byChain?.add(address);
      tokensByAddress.set(address, (tokensByAddress.get(address) ?? new Set()).add(token));
    });
    addressesByChain.set(chainId, byChain);
  });
  return Array.from(addressesByChain).flatMap(([chainId, addresses]) => {
    const chain = chains[chainId];
    if (!chain) {
      log.warn(`Chain ${chainId} not found`);
      return [];
    }
    const [chainMeta] = findChainMeta(miniMetadatas, "substrate-equilibrium", chain);
    const registry = chainMeta?.miniMetadata !== undefined && chainMeta?.miniMetadata !== null && chainMeta?.metadataVersion >= 14 ? getOrCreateTypeRegistry(chainId, chainMeta.miniMetadata) : new TypeRegistry();
    return Array.from(addresses).flatMap(address => {
      const storageHelper = new StorageHelper(registry, "system", "account", decodeAnyAddress(address));
      const stateKey = storageHelper.stateKey;
      const storageDecoder = chainStorageDecoders.get(chainId)?.storageDecoder;
      if (!stateKey) return [];
      const decodeResult = change => {
        // e.g.
        // {
        //   nonce: 5
        //   consumers: 0
        //   providers: 2
        //   sufficients: 0
        //   data: {
        //     V0: {
        //       lock: 0
        //       balance: [
        //         [
        //           25,969
        //           {
        //             Positive: 499,912,656,271
        //           }
        //         ]
        //         [
        //           6,582,132
        //           {
        //             Positive: 1,973,490,154
        //           }
        //         ]
        //         [
        //           6,648,164
        //           {
        //             Positive: 200,000,000
        //           }
        //         ]
        //         [
        //           435,694,104,436
        //           {
        //             Positive: 828,313,918
        //           }
        //         ]
        //       ]
        //     }
        //   }
        // }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const balances = storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) : null;
        const tokenBalances = Object.fromEntries((balances?.data?.balance ?? []
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ).map(balance => ({
          id: (balance?.[0] ?? 0n)?.toString?.(),
          free: balance?.[1]?.type === "Positive" ? (balance?.[1]?.value ?? 0n).toString() : balance?.[1]?.type === "Negative" ? ((balance?.[1]?.value ?? 0n) * -1n).toString() : "0"
        })).map(({
          id,
          free
        }) => [id, free]));
        return Array.from(tokensByAddress.get(address) ?? []).filter(t => t.chain.id === chainId).map(token => {
          const free = tokenBalances[token.assetId] ?? "0";
          return new Balance({
            source: "substrate-equilibrium",
            status: "live",
            address,
            multiChainId: {
              subChainId: chainId
            },
            chainId,
            tokenId: token.id,
            free
          });
        });
      };
      return {
        chainId,
        stateKey,
        decodeResult
      };
    });
  });
}
const DEFAULT_DECIMALS = 9;
const tokenSymbolFromU64Id = u64 => {
  const bytes = [];
  let num = typeof u64 === "number" ? BigInt(u64) : isBigInt(u64) ? u64 : u64.toBigInt();
  do {
    bytes.unshift(Number(num % 256n));
    num = num / 256n;
  } while (num > 0);
  return Buffer.from(bytes).toString("utf8").toUpperCase();
};

/**
 * Converts a subscription function into an Observable
 *
 * The type of a subscription function which can be converted into an observable:
 *
 *     <TArgs, TResult>(...arguments: TArgs, callback: SubscriptionCallback<TResult>) => UnsubscribeFn
 */
const asObservable = handler => (...args) => new Observable(subscriber => {
  const callback = (error, result) => error ? subscriber.error(error) : subscriber.next(result);
  const unsubscribe = handler(...args, callback);
  return unsubscribe;
});

/**
 * Each nominationPool in the nominationPools pallet has access to some accountIds which have no
 * associated private key. Instead, they are derived from this function.
 */
const nompoolAccountId = (registry, palletId, poolId, index) => {
  const EMPTY_H256 = new Uint8Array(32);
  const MOD_PREFIX = stringToU8a("modl");
  const U32_OPTS = {
    bitLength: 32,
    isLe: true
  };
  return registry.createType("AccountId32", u8aConcat(MOD_PREFIX, hexToU8a(palletId), new Uint8Array([index]), bnToU8a(new BN(poolId), U32_OPTS), EMPTY_H256)).toString();
};
/** The stash account for the nomination pool */
const nompoolStashAccountId = (registry, palletId, poolId) => nompoolAccountId(registry, palletId, poolId, 0);
/** The rewards account for the nomination pool */
const nompoolRewardAccountId = (registry, palletId, poolId) => nompoolAccountId(registry, palletId, poolId, 1);

/**
 * Crowdloan contributions are stored in the `childstate` key returned by this function.
 */
const crowdloanFundContributionsChildKey = (registry, fundIndex) => u8aToHex(u8aConcat(":child_storage:default:", blake2AsU8a(u8aConcat("crowdloan", registry.createType("u32", fundIndex).toU8a()))));
/**
 * For converting the value of `lock?.id?.toUtf8?.()` which is retrieved from
 * the Balances.Locks storage key into a useful classification for our UI
 */
const getLockedType = input => {
  if (typeof input !== "string") return "other";
  if (input.includes("vesting")) return "vesting";
  if (input.includes("calamvst")) return "vesting"; // vesting on manta network
  if (input.includes("ormlvest")) return "vesting"; // vesting ORML tokens
  if (input.includes("democrac")) return "democracy";
  if (input.includes("phrelect")) return "democracy"; // specific to council
  if (input.includes("staking")) return "staking";
  if (input.includes("stkngdel")) return "staking"; // staking delegator
  if (input.includes("stkngcol")) return "staking"; // staking collator
  if (input.includes("kiltpstk")) return "staking"; // Kilt specific staking
  if (input.includes("dapstake")) return "dapp-staking"; // Astar specific
  if (input.includes("appstake")) return "dapp-staking"; // Quartz (unique) specific

  // Joystream specifics https://github.com/Joystream/pioneer/blob/dev/packages/ui/src/accounts/model/lockTypes.ts
  if (input.includes("voting")) return "democracy";
  if (input.includes("candidac")) return "democracy"; // Council Candidate
  if (input.includes("councilo")) return "democracy"; // Councilor
  if (input.includes("proposal")) return "democracy";
  if (input.includes("boundsta")) return "staking"; // Bound Staking Account
  if (input.includes("invitemb")) return "other"; // Invite member
  if (input.includes("bounty")) return "other";
  if (input.startsWith("wg-")) return "other";

  // ignore technical or undocumented lock types
  if (input.includes("pdexlock")) return "other";
  if (input.includes("phala/sp")) return "other";

  // eslint-disable-next-line no-console
  console.warn(`unknown locked type: ${input}`);
  return "other";
};
const baseLockLabels = ["fees", "misc"];
const isBaseLock = lock => baseLockLabels.includes(lock.label);
const isNonBaseLock = lock => !isBaseLock(lock);
const filterBaseLocks = locks => {
  const hasNonBaseLocks = locks.some(isNonBaseLock);
  if (!hasNonBaseLocks) return locks;
  return locks.filter(isNonBaseLock);
};

// TODO: Make these titles translatable
const getLockTitle = (lock, {
  balance
} = {}) => {
  if (!lock.label) return lock.label;
  if (lock.label === "democracy") return "Governance";
  if (lock.label === "crowdloan") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (lock.meta?.type !== "crowdloan") return "Crowdloan";

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const paraId = lock.meta?.paraId;
    const name = balance?.chain?.parathreads?.find(parathread => parathread?.paraId === paraId)?.name;
    return `${name ? name : `Parachain ${paraId}`} Crowdloan`;
  }
  if (lock.label === "nompools-staking") return "Pooled Staking";
  if (lock.label === "nompools-unbonding") return "Pooled Staking";
  if (lock.label === "dapp-staking") return "DApp Staking";
  if (lock.label === "fees") return "Locked (Fees)";
  if (lock.label === "misc") return "Locked";
  if (lock.label === "other") return "Locked";
  return upperFirst(lock.label);
};

// AccountInfo is the state_storage data format for nativeToken balances
// Theory: new chains will be at least on metadata v14, and so we won't need to hardcode their AccountInfo type.
// But for chains we want to support which aren't on metadata v14, hardcode them here:
// If the chain upgrades to metadata v14, this override will be ignored :)
//
// TODO: Move the AccountInfoFallback configs for each chain into the ChainMeta section of chaindata
const RegularAccountInfoFallback = JSON.stringify({
  nonce: "u32",
  consumers: "u32",
  providers: "u32",
  sufficients: "u32",
  data: {
    free: "u128",
    reserved: "u128",
    miscFrozen: "u128",
    feeFrozen: "u128"
  }
});
const NoSufficientsAccountInfoFallback = JSON.stringify({
  nonce: "u32",
  consumers: "u32",
  providers: "u32",
  data: {
    free: "u128",
    reserved: "u128",
    miscFrozen: "u128",
    feeFrozen: "u128"
  }
});
const AccountInfoOverrides = {
  // automata is not yet on metadata v14
  "automata": RegularAccountInfoFallback,
  // crown-sterlin is not yet on metadata v14
  "crown-sterling": NoSufficientsAccountInfoFallback,
  // crust is not yet on metadata v14
  "crust": NoSufficientsAccountInfoFallback,
  // kulupu is not yet on metadata v14
  "kulupu": RegularAccountInfoFallback,
  // nftmart is not yet on metadata v14
  "nftmart": RegularAccountInfoFallback
};
const subNativeTokenId = chainId => `${chainId}-substrate-native`.toLowerCase().replace(/ /g, "-");
const getChainIdFromTokenId$2 = tokenId => {
  const match = /^([\d\w-]+)-substrate-native/.exec(tokenId);
  if (!match?.[1]) throw new Error(`Can't detect chainId for token ${tokenId}`);
  return match?.[1];
};
const SubNativeModule = hydrate => {
  const {
    chainConnectors,
    chaindataProvider
  } = hydrate;
  const chainConnector = chainConnectors.substrate;
  assert(chainConnector, "This module requires a substrate chain connector");
  const {
    getOrCreateTypeRegistry
  } = createTypeRegistryCache();
  return {
    ...DefaultBalanceModule("substrate-native"),
    async fetchSubstrateChainMeta(chainId, moduleConfig, metadataRpc) {
      const isTestnet = (await chaindataProvider.chainById(chainId))?.isTestnet || false;
      if (moduleConfig?.disable === true || metadataRpc === undefined) return {
        isTestnet,
        symbol: "",
        decimals: 0,
        existentialDeposit: null,
        nominationPoolsPalletId: null,
        crowdloanPalletId: null,
        miniMetadata: null,
        metadataVersion: 0
      };
      const chainProperties = await chainConnector.send(chainId, "system_properties", []);
      const metadataVersion = getMetadataVersion(metadataRpc);
      const {
        tokenSymbol,
        tokenDecimals
      } = chainProperties;
      const symbol = (Array.isArray(tokenSymbol) ? tokenSymbol[0] : tokenSymbol) ?? "Unit";
      const decimals = (Array.isArray(tokenDecimals) ? tokenDecimals[0] : tokenDecimals) ?? 0;
      if (metadataVersion !== 14) return {
        isTestnet,
        symbol,
        decimals,
        existentialDeposit: null,
        nominationPoolsPalletId: null,
        crowdloanPalletId: null,
        miniMetadata: null,
        metadataVersion
      };
      const metadata = $metadataV14.decode($.decodeHex(metadataRpc));
      const subshape = transformMetadataV14(metadata);
      const existentialDeposit = (subshape.pallets.Balances?.constants.ExistentialDeposit?.codec.decode?.(subshape.pallets.Balances.constants.ExistentialDeposit.value) ?? 0n).toString();
      const nominationPoolsPalletId = subshape.pallets.NominationPools?.constants.PalletId?.value ? Buffer.from(subshape.pallets.NominationPools?.constants.PalletId?.value).toString("hex") : null;
      const crowdloanPalletId = subshape.pallets.Crowdloan?.constants.PalletId?.value ? Buffer.from(subshape.pallets.Crowdloan?.constants.PalletId?.value).toString("hex") : null;
      const isSystemPallet = pallet => pallet.name === "System";
      const isAccountItem = item => item.name === "Account";
      const isBalancesPallet = pallet => pallet.name === "Balances";
      const isLocksItem = item => item.name === "Locks";
      const isNomPoolsPallet = pallet => pallet.name === "NominationPools";
      const isPoolMembersItem = item => item.name === "PoolMembers";
      const isBondedPoolsItem = item => item.name === "BondedPools";
      const isMetadataItem = item => item.name === "Metadata";
      const isStakingPallet = pallet => pallet.name === "Staking";
      const isLedgerItem = item => item.name === "Ledger";
      const isCrowdloanPallet = pallet => pallet.name === "Crowdloan";
      const isFundsItem = item => item.name === "Funds";
      const isParasPallet = pallet => pallet.name === "Paras";
      const isParachainsItem = item => item.name === "Parachains";

      // TODO: Handle metadata v15
      filterMetadataPalletsAndItems(metadata, [{
        pallet: isSystemPallet,
        items: [isAccountItem]
      }, {
        pallet: isBalancesPallet,
        items: [isLocksItem]
      }, {
        pallet: isNomPoolsPallet,
        items: [isPoolMembersItem, isBondedPoolsItem, isMetadataItem]
      }, {
        pallet: isStakingPallet,
        items: [isLedgerItem]
      }, {
        pallet: isCrowdloanPallet,
        items: [isFundsItem]
      }, {
        pallet: isParasPallet,
        items: [isParachainsItem]
      }]);
      metadata.extrinsic.signedExtensions = [];
      const miniMetadata = $.encodeHexPrefixed($metadataV14.encode(metadata));
      return {
        isTestnet,
        symbol,
        decimals,
        existentialDeposit,
        nominationPoolsPalletId,
        crowdloanPalletId,
        miniMetadata,
        metadataVersion
      };
    },
    async fetchSubstrateChainTokens(chainId, chainMeta, moduleConfig) {
      if (moduleConfig?.disable === true) return {};
      const {
        isTestnet,
        symbol,
        decimals,
        existentialDeposit
      } = chainMeta;
      const id = subNativeTokenId(chainId);
      const nativeToken = {
        id,
        type: "substrate-native",
        isTestnet,
        isDefault: moduleConfig?.isDefault ?? true,
        symbol,
        decimals,
        logo: moduleConfig?.logo || githubTokenLogoUrl(id),
        existentialDeposit: existentialDeposit ?? "0",
        chain: {
          id: chainId
        }
      };
      if (moduleConfig?.symbol) nativeToken.symbol = moduleConfig?.symbol;
      if (moduleConfig?.coingeckoId) nativeToken.coingeckoId = moduleConfig?.coingeckoId;
      if (moduleConfig?.dcentName) nativeToken.dcentName = moduleConfig?.dcentName;
      if (moduleConfig?.mirrorOf) nativeToken.mirrorOf = moduleConfig?.mirrorOf;
      return {
        [nativeToken.id]: nativeToken
      };
    },
    getPlaceholderBalance(tokenId, address) {
      const chainId = getChainIdFromTokenId$2(tokenId);
      return {
        source: "substrate-native",
        status: "initializing",
        address,
        multiChainId: {
          subChainId: chainId
        },
        chainId,
        tokenId,
        free: "0",
        reserves: [{
          label: "reserved",
          amount: "0"
        }],
        locks: [{
          label: "fees",
          amount: "0",
          includeInTransferable: true,
          excludeFromFeePayable: true
        }, {
          label: "misc",
          amount: "0"
        }]
      };
    },
    async subscribeBalances(addressesByToken, callback) {
      assert(chainConnectors.substrate, "This module requires a substrate chain connector");

      // TODO: Create a more elegant system which can handle the following use cases
      //
      // For now, we're just going to manually create three separate subscription handlers
      //
      // We can't just pass a bunch of queries into a single handler because for crowdloans
      // and staking we must first make some queries for some data and then create new queries
      // based on the result of those first queries
      // This is a usecase which is not yet managed by the RpcStateQueryHelper
      //
      // Eventually we should be able to delete the Deferred and just create the one
      // `subscribeBase` handler which handles everything

      const unsubDeferred = Deferred();
      // we return this to the caller so that they can let us know when they're no longer interested in this subscription
      const callerUnsubscribe = () => unsubDeferred.reject(new Error(`Caller unsubscribed`));
      // we queue up our work to clean up our subscription when this promise rejects
      const callerUnsubscribed = unsubDeferred.promise;
      subscribeNompoolStaking(chaindataProvider, chainConnectors.substrate, getOrCreateTypeRegistry, addressesByToken, callback, callerUnsubscribed);
      subscribeCrowdloans(chaindataProvider, chainConnectors.substrate, getOrCreateTypeRegistry, addressesByToken, callback, callerUnsubscribed);
      subscribeBase(chaindataProvider, chainConnectors.substrate, getOrCreateTypeRegistry, addressesByToken, callback, callerUnsubscribed);
      return callerUnsubscribe;
    },
    async fetchBalances(addressesByToken) {
      assert(chainConnectors.substrate, "This module requires a substrate chain connector");
      const queries = await buildQueries$1(chaindataProvider, getOrCreateTypeRegistry, addressesByToken);
      const result = await new RpcStateQueryHelper(chainConnectors.substrate, queries).fetch();
      return new Balances(result ?? []);
    },
    async transferToken({
      tokenId,
      from,
      to,
      amount,
      registry,
      metadataRpc,
      blockHash,
      blockNumber,
      nonce,
      specVersion,
      transactionVersion,
      tip,
      transferMethod
    }) {
      const token = await chaindataProvider.tokenById(tokenId);
      assert(token, `Token ${tokenId} not found in store`);
      if (token.type !== "substrate-native") throw new Error(`This module doesn't handle tokens of type ${token.type}`);
      const chainId = token.chain.id;
      const chain = await chaindataProvider.chainById(chainId);
      assert(chain?.genesisHash, `Chain ${chainId} not found in store`);
      const {
        genesisHash
      } = chain;
      const sendAll = transferMethod === "transferAll";
      let method = transferMethod;
      if (transferMethod === "transferAllowDeath") {
        try {
          method = detectTransferMethod(metadataRpc);
        } catch (cause) {
          log.debug(new Error(`An error occured while detecting the presence of the deprecated Balances::transfer call on chain ${chainId}`, {
            cause
          }));
        }
      }
      const pallet = "balances";
      const args = sendAll ? {
        dest: to,
        keepAlive: false
      } : {
        dest: to,
        value: amount
      };
      const unsigned = defineMethod({
        method: {
          pallet,
          name: method,
          args
        },
        address: from,
        blockHash,
        blockNumber,
        eraPeriod: 64,
        genesisHash,
        metadataRpc,
        nonce,
        specVersion,
        tip: tip ? Number(tip) : 0,
        transactionVersion
      }, {
        metadataRpc,
        registry
      });
      return {
        type: "substrate",
        tx: unsigned
      };
    }
  };
};
async function buildQueries$1(chaindataProvider, getOrCreateTypeRegistry, addressesByToken) {
  const chains = await chaindataProvider.chainsById();
  const tokens = await chaindataProvider.tokensById();
  const miniMetadatas = new Map((await db.miniMetadatas.toArray()).map(miniMetadata => [miniMetadata.id, miniMetadata]));
  const uniqueChainIds = getUniqueChainIds(addressesByToken, tokens);
  const chainStorageDecoders = buildStorageDecoders({
    chainIds: uniqueChainIds,
    chains,
    miniMetadatas,
    moduleType: "substrate-native",
    decoders: {
      baseDecoder: ["system", "account"],
      locksDecoder: ["balances", "locks"]
    }
  });
  return Object.entries(addressesByToken).flatMap(([tokenId, addresses]) => {
    const token = tokens[tokenId];
    if (!token) {
      log.warn(`Token ${tokenId} not found`);
      return [];
    }
    if (token.type !== "substrate-native") {
      log.debug(`This module doesn't handle tokens of type ${token.type}`);
      return [];
    }
    const chainId = token.chain?.id;
    if (!chainId) {
      log.warn(`Token ${tokenId} has no chain`);
      return [];
    }
    const chain = chains[chainId];
    if (!chain) {
      log.warn(`Chain ${chainId} for token ${tokenId} not found`);
      return [];
    }
    const [chainMeta] = findChainMeta(miniMetadatas, "substrate-native", chain);
    const hasMetadataV14 = chainMeta?.miniMetadata !== undefined && chainMeta?.miniMetadata !== null && chainMeta?.metadataVersion >= 14;
    const typeRegistry = hasMetadataV14 ? getOrCreateTypeRegistry(chainId, chainMeta.miniMetadata ?? undefined) : new TypeRegistry();
    return addresses.flatMap(address => {
      // We share thie balanceJson between the base and the lock query for this address
      //
      // TODO: Rearchitect the balance type (a tiny bit) so that it doesn't assume that one instance
      // of `Balance` is unique to one address and tokenId.
      //
      // Instead, we should allow for `address-tokenId-base` and `address-tokenId-lock` and `address-tokenId-staked` variations
      // of the one balance. Then calls to e.g. balances.find({ tokenId, address }).locked should be able to
      // do the correct locked calculation across the various Balance objects.
      //
      // This will allow us to handle caching etc on a Balance variation basis without messing up the locks calculation
      // (which takes the biggest lock for an address and tokenId, instead of adding the locks together)
      const balanceJson = {
        source: "substrate-native",
        status: "live",
        address,
        multiChainId: {
          subChainId: chainId
        },
        chainId,
        tokenId,
        free: "0",
        reserves: [{
          label: "reserved",
          amount: "0"
        }],
        locks: [{
          label: "fees",
          amount: "0",
          includeInTransferable: true,
          excludeFromFeePayable: true
        }, {
          label: "misc",
          amount: "0"
        }]
      };
      const baseQuery = (() => {
        const storageHelper = new StorageHelper(typeRegistry, "system", "account", decodeAnyAddress(address));
        const storageDecoder = chainStorageDecoders.get(chainId)?.baseDecoder;
        const getFallbackStateKey = () => {
          const addressBytes = decodeAnyAddress(address);
          const addressHash = blake2Concat(addressBytes).replace(/^0x/, "");
          const moduleHash = "26aa394eea5630e07c48ae0c9558cef7"; // util_crypto.xxhashAsHex("System", 128);
          const storageHash = "b99d880ec681799c0cf30e8886371da9"; // util_crypto.xxhashAsHex("Account", 128);
          const moduleStorageHash = `${moduleHash}${storageHash}`; // System.Account is the state_storage key prefix for nativeToken balances
          return `0x${moduleStorageHash}${addressHash}`;
        };

        /**
         * NOTE: For many MetadataV14 chains, it is not valid to encode an ethereum address into this System.Account state call.
         * However, because we have always made that state call in the past, existing users will have the result (a balance of `0`)
         * cached in their BalancesDB.
         *
         * So, until we refactor the storage of this module in a way which nukes the existing cached balances, we'll need to continue
         * making these invalid state calls to keep those balances from showing as `cached` or `stale`.
         *
         * Current logic:
         *
         *     stateKey: string = hasMetadataV14 && storageHelper.stateKey ? storageHelper.stateKey : getFallbackStateKey()
         *
         * Future (ideal) logic:
         *
         *     stateKey: string | undefined = hasMetadataV14 ? storageHelper.stateKey : getFallbackStateKey()
         */
        const stateKey = hasMetadataV14 && storageHelper.stateKey ? storageHelper.stateKey : getFallbackStateKey();
        const decodeResult = change => {
          // BEGIN: Handle chains which use metadata < v14
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let oldChainBalance = undefined;
          if (!hasMetadataV14) {
            const accountInfoTypeDef = AccountInfoOverrides[chainId];
            if (accountInfoTypeDef === undefined) {
              // chain metadata version is < 14 and we also don't have an override hardcoded in
              // the best way to handle this case: log a warning and return an empty balance
              log.debug(`Token ${tokenId} on chain ${chainId} has no balance type for decoding. Defaulting to a balance of 0 (zero).`);
              return new Balance(balanceJson);
            }
            try {
              // eslint-disable-next-line no-var
              oldChainBalance = createType(typeRegistry, accountInfoTypeDef, change);
            } catch (error) {
              log.warn(`Failed to create pre-metadataV14 balance type for token ${tokenId} on chain ${chainId}: ${error?.toString()}`);
              return new Balance(balanceJson);
            }
          }
          // END: Handle chains which use metadata < v14

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const decoded = hasMetadataV14 && storageDecoder ? change === null ? null : storageDecoder.decode($.decodeHex(change)) : oldChainBalance;
          const bigIntOrCodecToBigInt = value => typeof value === "bigint" ? value : value?.toBigInt?.();
          let free = (bigIntOrCodecToBigInt(decoded?.data?.free) ?? 0n).toString();
          let reserved = (bigIntOrCodecToBigInt(decoded?.data?.reserved) ?? 0n).toString();
          let miscFrozen = (bigIntOrCodecToBigInt(decoded?.data?.miscFrozen) ?? 0n).toString();
          let feeFrozen = (bigIntOrCodecToBigInt(decoded?.data?.feeFrozen) ?? 0n).toString();

          // we use the evm-native module to fetch native token balances for ethereum addresses on ethereum networks
          // but on moonbeam, moonriver and other chains which use ethereum addresses instead of substrate addresses,
          // we use both this module and the evm-native module
          if (isEthereumAddress(address) && chain.account !== "secp256k1") free = reserved = miscFrozen = feeFrozen = "0";
          balanceJson.free = free;
          const otherReserve = balanceJson.reserves.find(({
            label
          }) => label === "reserved");
          if (otherReserve) otherReserve.amount = reserved;
          const feesLock = balanceJson.locks.find(({
            label
          }) => label === "fees");
          if (feesLock) feesLock.amount = feeFrozen;
          const miscLock = balanceJson.locks.find(({
            label
          }) => label === "misc");
          if (miscLock) miscLock.amount = miscFrozen;
          return new Balance(balanceJson);
        };
        return {
          chainId,
          stateKey,
          decodeResult
        };
      })();
      const locksQuery = (() => {
        const storageHelper = new StorageHelper(typeRegistry, "balances", "locks", decodeAnyAddress(address));
        const storageDecoder = chainStorageDecoders.get(chainId)?.locksDecoder;
        const stateKey = storageHelper.stateKey;
        if (!stateKey) return;
        const decodeResult = change => {
          if (change === null) return new Balance(balanceJson);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const decoded = storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) : null;
          balanceJson.locks = [...balanceJson.locks.slice(0, 2),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(decoded?.map?.(lock => ({
            label: getLockedType(lock?.id?.toUtf8?.()),
            amount: lock?.amount?.toString?.() ?? "0"
          })) ?? [])];
          return new Balance(balanceJson);
        };
        return {
          chainId,
          stateKey,
          decodeResult
        };
      })();
      const queries = [baseQuery, locksQuery].filter(query => Boolean(query));
      return queries;
    });
  });
}
async function subscribeNompoolStaking(chaindataProvider, chainConnector, getOrCreateTypeRegistry, addressesByToken, callback, callerUnsubscribed) {
  const chains = await chaindataProvider.chainsById();
  const tokens = await chaindataProvider.tokensById();
  const miniMetadatas = new Map((await db.miniMetadatas.toArray()).map(miniMetadata => [miniMetadata.id, miniMetadata]));
  const nomPoolTokenIds = Object.entries(tokens).filter(([, token]) => {
    // ignore non-native tokens
    if (token.type !== "substrate-native") return false;
    // ignore tokens on chains with no nompools pallet
    const [chainMeta] = findChainMeta(miniMetadatas, "substrate-native", chains[token.chain.id]);
    return typeof chainMeta?.nominationPoolsPalletId === "string";
  }).map(([tokenId]) => tokenId);

  // staking can only be done by the native token on chains with the staking pallet
  const addressesByNomPoolToken = Object.fromEntries(Object.entries(addressesByToken)
  // remove ethereum addresses
  .map(([tokenId, addresses]) => [tokenId, addresses.filter(address => !isEthereumAddress(address))])
  // remove tokens which aren't nom pool tokens
  .filter(([tokenId]) => nomPoolTokenIds.includes(tokenId)));
  const uniqueChainIds = getUniqueChainIds(addressesByNomPoolToken, tokens);
  const chainStorageDecoders = buildStorageDecoders({
    chainIds: uniqueChainIds,
    chains,
    miniMetadatas,
    moduleType: "substrate-native",
    decoders: {
      poolMembersDecoder: ["nominationPools", "poolMembers"],
      bondedPoolsDecoder: ["nominationPools", "bondedPools"],
      ledgerDecoder: ["staking", "ledger"],
      metadataDecoder: ["nominationPools", "metadata"]
    }
  });
  for (const [tokenId, addresses] of Object.entries(addressesByNomPoolToken)) {
    const token = tokens[tokenId];
    if (!token) {
      log.warn(`Token ${tokenId} not found`);
      continue;
    }
    if (token.type !== "substrate-native") {
      log.debug(`This module doesn't handle tokens of type ${token.type}`);
      continue;
    }
    const chainId = token.chain?.id;
    if (!chainId) {
      log.warn(`Token ${tokenId} has no chain`);
      continue;
    }
    const chain = chains[chainId];
    if (!chain) {
      log.warn(`Chain ${chainId} for token ${tokenId} not found`);
      continue;
    }
    const [chainMeta] = findChainMeta(miniMetadatas, "substrate-native", chain);
    const typeRegistry = chainMeta?.miniMetadata !== undefined && chainMeta?.miniMetadata !== null && chainMeta?.metadataVersion >= 14 ? getOrCreateTypeRegistry(chainId, chainMeta.miniMetadata) : new TypeRegistry();
    const subscribePoolMembers = (addresses, callback) => {
      const storageDecoder = chainStorageDecoders.get(chainId)?.poolMembersDecoder;
      const queries = addresses.flatMap(address => {
        const storageHelper = new StorageHelper(typeRegistry, "nominationPools", "poolMembers", decodeAnyAddress(address));
        const stateKey = storageHelper.stateKey;
        if (!stateKey) return [];
        const decodeResult = change => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const decoded = storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) : null;
          const poolId = decoded?.poolId?.toString?.();
          const points = decoded?.points?.toString?.();
          const unbondingEras =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Array.from(decoded?.unbondingEras ?? []).flatMap(entry => {
            const [key, value] = Array.from(entry);
            const era = key?.toString?.();
            const amount = value?.toString?.();
            if (typeof era !== "string" || typeof amount !== "string") return [];
            return {
              era,
              amount
            };
          });
          return {
            tokenId,
            address,
            poolId,
            points,
            unbondingEras
          };
        };
        return {
          chainId,
          stateKey,
          decodeResult
        };
      });
      const subscription = new RpcStateQueryHelper(chainConnector, queries).subscribe(callback);
      return () => subscription.then(unsubscribe => unsubscribe());
    };
    const subscribePoolPoints = (poolIds, callback) => {
      if (poolIds.length === 0) callback(null, []);
      const storageDecoder = chainStorageDecoders.get(chainId)?.bondedPoolsDecoder;
      const queries = poolIds.flatMap(poolId => {
        const storageHelper = new StorageHelper(typeRegistry, "nominationPools", "bondedPools", poolId);
        const stateKey = storageHelper.stateKey;
        if (!stateKey) return [];
        const decodeResult = change => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const decoded = storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) : null;
          const points = decoded?.points?.toString?.();
          return {
            poolId,
            points
          };
        };
        return {
          chainId,
          stateKey,
          decodeResult
        };
      });
      const subscription = new RpcStateQueryHelper(chainConnector, queries).subscribe(callback);
      return () => subscription.then(unsubscribe => unsubscribe());
    };
    const subscribePoolStake = (poolIds, callback) => {
      if (poolIds.length === 0) callback(null, []);
      const storageDecoder = chainStorageDecoders.get(chainId)?.ledgerDecoder;
      const queries = poolIds.flatMap(poolId => {
        if (!chainMeta?.nominationPoolsPalletId) return [];
        const storageHelper = new StorageHelper(typeRegistry, "staking", "ledger", nompoolStashAccountId(typeRegistry, chainMeta?.nominationPoolsPalletId, poolId));
        const stateKey = storageHelper.stateKey;
        if (!stateKey) return [];
        const decodeResult = change => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const decoded = storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) : null;
          const activeStake = decoded?.active?.toString?.();
          return {
            poolId,
            activeStake
          };
        };
        return {
          chainId,
          stateKey,
          decodeResult
        };
      });
      const subscription = new RpcStateQueryHelper(chainConnector, queries).subscribe(callback);
      return () => subscription.then(unsubscribe => unsubscribe());
    };
    const subscribePoolMetadata = (poolIds, callback) => {
      if (poolIds.length === 0) callback(null, []);
      const storageDecoder = chainStorageDecoders.get(chainId)?.metadataDecoder;
      const queries = poolIds.flatMap(poolId => {
        if (!chainMeta?.nominationPoolsPalletId) return [];
        const storageHelper = new StorageHelper(typeRegistry, "nominationPools", "metadata", poolId);
        const stateKey = storageHelper.stateKey;
        if (!stateKey) return [];
        const decodeResult = change => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const decoded = storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) : null;
          const metadata = u8aToString(decoded);
          return {
            poolId,
            metadata
          };
        };
        return {
          chainId,
          stateKey,
          decodeResult
        };
      });
      const subscription = new RpcStateQueryHelper(chainConnector, queries).subscribe(callback);
      return () => subscription.then(unsubscribe => unsubscribe());
    };
    const poolMembersByAddress$ = asObservable(subscribePoolMembers)(addresses).pipe(scan((state, next) => {
      for (const poolMembers of next) {
        const {
          poolId,
          points,
          unbondingEras
        } = poolMembers;
        if (typeof poolId === "string" && typeof points === "string") state.set(poolMembers.address, {
          poolId,
          points,
          unbondingEras
        });else state.set(poolMembers.address, null);
      }
      return state;
    }, new Map()), share());
    const poolIdByAddress$ = poolMembersByAddress$.pipe(map(pm => new Map(Array.from(pm).map(([address, pm]) => [address, pm?.poolId ?? null]))));
    const pointsByAddress$ = poolMembersByAddress$.pipe(map(pm => new Map(Array.from(pm).map(([address, pm]) => [address, pm?.points ?? null]))));
    const unbondingErasByAddress$ = poolMembersByAddress$.pipe(map(pm => new Map(Array.from(pm).map(([address, pm]) => [address, pm?.unbondingEras ?? null]))));
    const poolIds$ = poolIdByAddress$.pipe(map(byAddress => [...new Set(Array.from(byAddress.values()).flatMap(poolId => poolId ?? []))]));
    const pointsByPool$ = poolIds$.pipe(map(poolIds => asObservable(subscribePoolPoints)(poolIds)), switchAll(), scan((state, next) => {
      for (const poolPoints of next) {
        const {
          poolId,
          points
        } = poolPoints;
        if (typeof points === "string") state.set(poolId, points);else state.delete(poolId);
      }
      return state;
    }, new Map()));
    const stakeByPool$ = poolIds$.pipe(map(poolIds => asObservable(subscribePoolStake)(poolIds)), switchAll(), scan((state, next) => {
      for (const poolStake of next) {
        const {
          poolId,
          activeStake
        } = poolStake;
        if (typeof activeStake === "string") state.set(poolId, activeStake);else state.delete(poolId);
      }
      return state;
    }, new Map()));
    const metadataByPool$ = poolIds$.pipe(map(poolIds => asObservable(subscribePoolMetadata)(poolIds)), switchAll(), scan((state, next) => {
      for (const poolMetadata of next) {
        const {
          poolId,
          metadata
        } = poolMetadata;
        if (typeof metadata === "string") state.set(poolId, metadata);else state.delete(poolId);
      }
      return state;
    }, new Map()));
    const subscription = combineLatest([poolIdByAddress$, pointsByAddress$, unbondingErasByAddress$, pointsByPool$, stakeByPool$, metadataByPool$]).subscribe({
      next: ([poolIdByAddress, pointsByAddress, unbondingErasByAddress, pointsByPool, stakeByPool, metadataByPool]) => {
        const balances = Array.from(poolIdByAddress).map(([address, poolId]) => {
          const parsedPoolId = poolId === null ? undefined : parseInt(poolId);
          const points = pointsByAddress.get(address) ?? "0";
          const poolPoints = pointsByPool.get(poolId ?? "") ?? "0";
          const poolStake = stakeByPool.get(poolId ?? "") ?? "0";
          const poolMetadata = poolId ? metadataByPool.get(poolId) ?? `Pool ${poolId}` : undefined;
          const amount = points === "0" || poolPoints === "0" || poolStake === "0" ? "0" : (BigInt(poolStake) * BigInt(points) / BigInt(poolPoints)).toString();
          const unbondingAmount = (unbondingErasByAddress.get(address) ?? []).reduce((total, {
            amount
          }) => total + BigInt(amount ?? "0"), 0n).toString();
          return {
            source: "substrate-native",
            subSource: "nompools-staking",
            status: "live",
            address,
            multiChainId: {
              subChainId: chainId
            },
            chainId,
            tokenId,
            free: "0",
            reserves: [{
              label: "reserved",
              amount: "0"
            }, {
              label: "nompools-staking",
              amount,
              meta: {
                type: "nompool",
                poolId: parsedPoolId,
                description: poolMetadata
              }
            }, {
              label: "nompools-unbonding",
              amount: unbondingAmount,
              meta: {
                type: "nompool",
                poolId: parsedPoolId,
                description: poolMetadata,
                unbonding: true
              }
            }],
            locks: [{
              label: "fees",
              amount: "0",
              includeInTransferable: true,
              excludeFromFeePayable: true
            }, {
              label: "misc",
              amount: "0"
            }]
          };
        });
        callback(null, new Balances(balances ?? []));
      },
      error: error => callback(error)
    });
    callerUnsubscribed.catch(() => subscription.unsubscribe()).catch(error => log.warn(error));
  }
}
async function subscribeCrowdloans(chaindataProvider, chainConnector, getOrCreateTypeRegistry, addressesByToken, callback, callerUnsubscribed) {
  const chains = await chaindataProvider.chainsById();
  const tokens = await chaindataProvider.tokensById();
  const miniMetadatas = new Map((await db.miniMetadatas.toArray()).map(miniMetadata => [miniMetadata.id, miniMetadata]));
  const crowdloanTokenIds = Object.entries(tokens).filter(([, token]) => {
    // ignore non-native tokens
    if (token.type !== "substrate-native") return;
    // ignore tokens on chains with no crowdloans pallet
    const [chainMeta] = findChainMeta(miniMetadatas, "substrate-native", chains[token.chain.id]);
    return typeof chainMeta?.crowdloanPalletId === "string";
  }).map(([tokenId]) => tokenId);

  // crowdloan contributions can only be done by the native token on chains with the crowdloan pallet
  const addressesByCrowdloanToken = Object.fromEntries(Object.entries(addressesByToken)
  // remove ethereum addresses
  .map(([tokenId, addresses]) => [tokenId, addresses.filter(address => !isEthereumAddress(address))])
  // remove tokens which aren't crowdloan tokens
  .filter(([tokenId]) => crowdloanTokenIds.includes(tokenId)));
  const uniqueChainIds = getUniqueChainIds(addressesByCrowdloanToken, tokens);
  const chainStorageDecoders = buildStorageDecoders({
    chainIds: uniqueChainIds,
    chains,
    miniMetadatas,
    moduleType: "substrate-native",
    decoders: {
      parachainsDecoder: ["paras", "parachains"],
      fundsDecoder: ["crowdloan", "funds"]
    }
  });
  for (const [tokenId, addresses] of Object.entries(addressesByCrowdloanToken)) {
    const token = tokens[tokenId];
    if (!token) {
      log.warn(`Token ${tokenId} not found`);
      continue;
    }
    if (token.type !== "substrate-native") {
      log.debug(`This module doesn't handle tokens of type ${token.type}`);
      continue;
    }
    const chainId = token.chain?.id;
    if (!chainId) {
      log.warn(`Token ${tokenId} has no chain`);
      continue;
    }
    const chain = chains[chainId];
    if (!chain) {
      log.warn(`Chain ${chainId} for token ${tokenId} not found`);
      continue;
    }
    const [chainMeta] = findChainMeta(miniMetadatas, "substrate-native", chain);
    const typeRegistry = chainMeta?.miniMetadata !== undefined && chainMeta?.miniMetadata !== null && chainMeta?.metadataVersion >= 14 ? getOrCreateTypeRegistry(chainId, chainMeta.miniMetadata) : new TypeRegistry();
    const subscribeParaIds = callback => {
      const storageDecoder = chainStorageDecoders.get(chainId)?.parachainsDecoder;
      const queries = [0].flatMap(() => {
        const storageHelper = new StorageHelper(typeRegistry, "paras", "parachains");
        const stateKey = storageHelper.stateKey;
        if (!stateKey) return [];
        const decodeResult = change => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const decoded = storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) : null;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const paraIds = decoded ?? [];
          return paraIds;
        };
        return {
          chainId,
          stateKey,
          decodeResult
        };
      });
      const subscription = new RpcStateQueryHelper(chainConnector, queries).subscribe(callback);
      return () => subscription.then(unsubscribe => unsubscribe());
    };
    const subscribeParaFundIndexes = (paraIds, callback) => {
      const storageDecoder = chainStorageDecoders.get(chainId)?.fundsDecoder;
      const queries = paraIds.flatMap(paraId => {
        const storageHelper = new StorageHelper(typeRegistry, "crowdloan", "funds", paraId);
        const stateKey = storageHelper.stateKey;
        if (!stateKey) return [];
        const decodeResult = change => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const decoded = storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) : null;
          const firstPeriod = decoded?.firstPeriod?.toString?.() ?? "";
          const lastPeriod = decoded?.lastPeriod?.toString?.() ?? "";
          const fundPeriod = `${firstPeriod}-${lastPeriod}`;
          const fundIndex = decoded?.fundIndex ?? decoded?.trieIndex;
          return {
            paraId,
            fundPeriod,
            fundIndex
          };
        };
        return {
          chainId,
          stateKey,
          decodeResult
        };
      });
      const subscription = new RpcStateQueryHelper(chainConnector, queries).subscribe(callback);
      return () => subscription.then(unsubscribe => unsubscribe());
    };
    const subscribeFundContributions = (funds, addresses, callback) => {
      // TODO: Watch system_events in order to subscribe to changes, then redo the contributions query when changes are detected:
      // https://github.com/polkadot-js/api/blob/8fe02a14345b57e6abb8f7f2c2b624cf70c51b23/packages/api-derive/src/crowdloan/ownContributions.ts#L32-L47
      //
      // For now we just re-fetch all contributions on a timer and then only send them to the subscription callback when they have changed

      const queries = funds.map(({
        paraId,
        fundIndex
      }) => ({
        paraId,
        fundIndex,
        addresses,
        childKey: crowdloanFundContributionsChildKey(typeRegistry, fundIndex),
        storageKeys: addresses.map(address => u8aToHex(decodeAnyAddress(address)))
      }));

      // track whether our caller is still subscribed
      let subscriptionActive = true;
      let previousContributions = null;
      const fetchContributions = async () => {
        try {
          const results = await Promise.all(queries.map(async ({
            paraId,
            fundIndex,
            addresses,
            childKey,
            storageKeys
          }) => ({
            paraId,
            fundIndex,
            addresses,
            result: await chainConnector.send(chainId, "childstate_getStorageEntries", [childKey, storageKeys])
          })));
          const contributions = results.flatMap(queryResult => {
            const {
              paraId,
              fundIndex,
              addresses,
              result
            } = queryResult;
            const storageDataVec = typeRegistry.createType("Vec<Option<StorageData>>", result);
            return storageDataVec.flatMap((storageData, index) => {
              const balance = storageData?.isSome ? typeRegistry.createType("Balance", storageData.unwrap()) : typeRegistry.createType("Balance");
              const amount = balance?.toString?.();
              if (amount === undefined || amount === "0") return [];
              return {
                paraId,
                fundIndex,
                address: addresses[index],
                amount
              };
            });
          });

          // ignore these results if our caller has tried to close this subscription
          if (!subscriptionActive) return;

          // ignore these results if they're the same as what we previously fetched
          if (isEqual(previousContributions, contributions)) return;
          previousContributions = contributions;
          callback(null, contributions);
        } catch (error) {
          callback(error);
        }
      };

      // set up polling for contributions
      const crowdloanContributionsPollInterval = 60_000; // 60_000ms === 1 minute
      const pollContributions = async () => {
        if (!subscriptionActive) return;
        try {
          await fetchContributions();
        } catch (error) {
          // log any errors, but don't cancel the poll for contributions when one fetch fails
          log.error(error);
        }
        if (!subscriptionActive) return;
        setTimeout(pollContributions, crowdloanContributionsPollInterval);
      };

      // start polling
      pollContributions();
      return () => {
        // stop polling
        subscriptionActive = false;
      };
    };
    const paraIds$ = asObservable(subscribeParaIds)().pipe(scan((_, next) => Array.from(new Set(next.flatMap(paraIds => paraIds))), []), share());
    const fundIndexesByParaId$ = paraIds$.pipe(map(paraIds => asObservable(subscribeParaFundIndexes)(paraIds)), switchAll(), scan((state, next) => {
      for (const fund of next) {
        const {
          paraId,
          fundIndex
        } = fund;
        if (typeof fundIndex === "number") {
          state.set(paraId, (state.get(paraId) ?? new Set()).add(fundIndex));
        }
      }
      return state;
    }, new Map()));
    const contributionsByAddress$ = fundIndexesByParaId$.pipe(map(fundIndexesByParaId => Array.from(fundIndexesByParaId).flatMap(([paraId, fundIndexes]) => Array.from(fundIndexes).map(fundIndex => ({
      paraId,
      fundIndex
    })))), map(funds => asObservable(subscribeFundContributions)(funds, addresses)), switchAll(), scan((state, next) => {
      for (const contribution of next) {
        const {
          address
        } = contribution;
        state.set(address, (state.get(address) ?? new Set()).add(contribution));
      }
      return state;
    }, new Map()));
    const subscription = contributionsByAddress$.subscribe({
      next: contributionsByAddress => {
        const balances = Array.from(contributionsByAddress).map(([address, contributions]) => {
          return {
            source: "substrate-native",
            subSource: "crowdloan",
            status: "live",
            address,
            multiChainId: {
              subChainId: chainId
            },
            chainId,
            tokenId,
            free: "0",
            reserves: [{
              label: "reserved",
              amount: "0"
            }, ...Array.from(contributions).map(({
              amount,
              paraId
            }) => ({
              label: "crowdloan",
              amount: amount,
              meta: {
                type: "crowdloan",
                paraId
              }
            }))],
            locks: [{
              label: "fees",
              amount: "0",
              includeInTransferable: true,
              excludeFromFeePayable: true
            }, {
              label: "misc",
              amount: "0"
            }]
          };
        });
        callback(null, new Balances(balances ?? []));
      },
      error: error => callback(error)
    });
    callerUnsubscribed.catch(() => subscription.unsubscribe()).catch(error => log.warn(error));
  }
}
async function subscribeBase(chaindataProvider, chainConnector, getOrCreateTypeRegistry, addressesByToken, callback, callerUnsubscribed) {
  const queries = await buildQueries$1(chaindataProvider, getOrCreateTypeRegistry, addressesByToken);
  const unsubscribe = await new RpcStateQueryHelper(chainConnector, queries).subscribe((error, result) => error ? callback(error) : callback(null, new Balances(result ?? [])));
  callerUnsubscribed.catch(unsubscribe).catch(error => log.warn(error));
}

var psp22Abi = {
	source: {
		hash: "0x9adaf0c0a3d26ea6dc77ef8cbf1a682100a7ab3f3cd0d3d6022ace606b7484a7",
		language: "ink! 4.0.1",
		compiler: "rustc 1.69.0-nightly",
		build_info: {
			build_mode: "Debug",
			cargo_contract_version: "2.2.1",
			rust_toolchain: "nightly-x86_64-unknown-linux-gnu",
			wasm_opt_settings: {
				keep_debug_symbols: false,
				optimization_passes: "Z"
			}
		}
	},
	contract: {
		name: "psp22",
		version: "1.0.0",
		authors: [
			"OpenBrush"
		]
	},
	spec: {
		constructors: [
			{
				args: [
					{
						label: "initial_supply",
						type: {
							displayName: [
								"Balance"
							],
							type: 0
						}
					},
					{
						label: "name",
						type: {
							displayName: [
								"Option"
							],
							type: 4
						}
					},
					{
						label: "symbol",
						type: {
							displayName: [
								"Option"
							],
							type: 4
						}
					},
					{
						label: "decimal",
						type: {
							displayName: [
								"u8"
							],
							type: 3
						}
					}
				],
				docs: [
				],
				label: "new",
				payable: false,
				returnType: {
					displayName: [
						"ink_primitives",
						"ConstructorResult"
					],
					type: 5
				},
				selector: "0x9bae9d5e"
			}
		],
		docs: [
		],
		events: [
		],
		lang_error: {
			displayName: [
				"ink",
				"LangError"
			],
			type: 6
		},
		messages: [
			{
				args: [
					{
						label: "owner",
						type: {
							displayName: [
								"psp22_external",
								"BalanceOfInput1"
							],
							type: 7
						}
					}
				],
				docs: [
					" Returns the account Balance for the specified `owner`.",
					"",
					" Returns `0` if the account is non-existent."
				],
				label: "PSP22::balance_of",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 9
				},
				selector: "0x6568382f"
			},
			{
				args: [
					{
						label: "spender",
						type: {
							displayName: [
								"psp22_external",
								"ApproveInput1"
							],
							type: 7
						}
					},
					{
						label: "value",
						type: {
							displayName: [
								"psp22_external",
								"ApproveInput2"
							],
							type: 0
						}
					}
				],
				docs: [
					" Allows `spender` to withdraw from the caller's account multiple times, up to",
					" the `value` amount.",
					"",
					" If this function is called again it overwrites the current allowance with `value`.",
					"",
					" An `Approval` event is emitted.",
					"",
					" # Errors",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::approve",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0xb20f1bbd"
			},
			{
				args: [
					{
						label: "to",
						type: {
							displayName: [
								"psp22_external",
								"TransferInput1"
							],
							type: 7
						}
					},
					{
						label: "value",
						type: {
							displayName: [
								"psp22_external",
								"TransferInput2"
							],
							type: 0
						}
					},
					{
						label: "data",
						type: {
							displayName: [
								"psp22_external",
								"TransferInput3"
							],
							type: 2
						}
					}
				],
				docs: [
					" Transfers `value` amount of tokens from the caller's account to account `to`",
					" with additional `data` in unspecified format.",
					"",
					" On success a `Transfer` event is emitted.",
					"",
					" # Errors",
					"",
					" Returns `InsufficientBalance` error if there are not enough tokens on",
					" the caller's account Balance.",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::transfer",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0xdb20f9f5"
			},
			{
				args: [
					{
						label: "from",
						type: {
							displayName: [
								"psp22_external",
								"TransferFromInput1"
							],
							type: 7
						}
					},
					{
						label: "to",
						type: {
							displayName: [
								"psp22_external",
								"TransferFromInput2"
							],
							type: 7
						}
					},
					{
						label: "value",
						type: {
							displayName: [
								"psp22_external",
								"TransferFromInput3"
							],
							type: 0
						}
					},
					{
						label: "data",
						type: {
							displayName: [
								"psp22_external",
								"TransferFromInput4"
							],
							type: 2
						}
					}
				],
				docs: [
					" Transfers `value` tokens on the behalf of `from` to the account `to`",
					" with additional `data` in unspecified format.",
					"",
					" This can be used to allow a contract to transfer tokens on ones behalf and/or",
					" to charge fees in sub-currencies, for example.",
					"",
					" On success a `Transfer` and `Approval` events are emitted.",
					"",
					" # Errors",
					"",
					" Returns `InsufficientAllowance` error if there are not enough tokens allowed",
					" for the caller to withdraw from `from`.",
					"",
					" Returns `InsufficientBalance` error if there are not enough tokens on",
					" the the account Balance of `from`.",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::transfer_from",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0x54b3c76e"
			},
			{
				args: [
					{
						label: "spender",
						type: {
							displayName: [
								"psp22_external",
								"IncreaseAllowanceInput1"
							],
							type: 7
						}
					},
					{
						label: "delta_value",
						type: {
							displayName: [
								"psp22_external",
								"IncreaseAllowanceInput2"
							],
							type: 0
						}
					}
				],
				docs: [
					" Atomically increases the allowance granted to `spender` by the caller.",
					"",
					" An `Approval` event is emitted.",
					"",
					" # Errors",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::increase_allowance",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0x96d6b57a"
			},
			{
				args: [
				],
				docs: [
					" Returns the total token supply."
				],
				label: "PSP22::total_supply",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 9
				},
				selector: "0x162df8c2"
			},
			{
				args: [
					{
						label: "spender",
						type: {
							displayName: [
								"psp22_external",
								"DecreaseAllowanceInput1"
							],
							type: 7
						}
					},
					{
						label: "delta_value",
						type: {
							displayName: [
								"psp22_external",
								"DecreaseAllowanceInput2"
							],
							type: 0
						}
					}
				],
				docs: [
					" Atomically decreases the allowance granted to `spender` by the caller.",
					"",
					" An `Approval` event is emitted.",
					"",
					" # Errors",
					"",
					" Returns `InsufficientAllowance` error if there are not enough tokens allowed",
					" by owner for `spender`.",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::decrease_allowance",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0xfecb57d5"
			},
			{
				args: [
					{
						label: "owner",
						type: {
							displayName: [
								"psp22_external",
								"AllowanceInput1"
							],
							type: 7
						}
					},
					{
						label: "spender",
						type: {
							displayName: [
								"psp22_external",
								"AllowanceInput2"
							],
							type: 7
						}
					}
				],
				docs: [
					" Returns the amount which `spender` is still allowed to withdraw from `owner`.",
					"",
					" Returns `0` if no allowance has been set `0`."
				],
				label: "PSP22::allowance",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 9
				},
				selector: "0x4d47d921"
			},
			{
				args: [
				],
				docs: [
					" Returns the token symbol."
				],
				label: "PSP22Metadata::token_symbol",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 13
				},
				selector: "0x34205be5"
			},
			{
				args: [
				],
				docs: [
					" Returns the token name."
				],
				label: "PSP22Metadata::token_name",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 13
				},
				selector: "0x3d261bd4"
			},
			{
				args: [
				],
				docs: [
					" Returns the token decimals."
				],
				label: "PSP22Metadata::token_decimals",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 14
				},
				selector: "0x7271b782"
			}
		]
	},
	storage: {
		root: {
			layout: {
				struct: {
					fields: [
						{
							layout: {
								struct: {
									fields: [
										{
											layout: {
												leaf: {
													key: "0x00000000",
													ty: 0
												}
											},
											name: "supply"
										},
										{
											layout: {
												root: {
													layout: {
														leaf: {
															key: "0x1d458d3b",
															ty: 0
														}
													},
													root_key: "0x1d458d3b"
												}
											},
											name: "balances"
										},
										{
											layout: {
												root: {
													layout: {
														leaf: {
															key: "0x0abd72fb",
															ty: 0
														}
													},
													root_key: "0x0abd72fb"
												}
											},
											name: "allowances"
										},
										{
											layout: {
												"enum": {
													dispatchKey: "0x00000000",
													name: "Option",
													variants: {
														"0": {
															fields: [
															],
															name: "None"
														},
														"1": {
															fields: [
																{
																	layout: {
																		leaf: {
																			key: "0x00000000",
																			ty: 1
																		}
																	},
																	name: "0"
																}
															],
															name: "Some"
														}
													}
												}
											},
											name: "_reserved"
										}
									],
									name: "Data"
								}
							},
							name: "psp22"
						},
						{
							layout: {
								struct: {
									fields: [
										{
											layout: {
												"enum": {
													dispatchKey: "0x00000000",
													name: "Option",
													variants: {
														"0": {
															fields: [
															],
															name: "None"
														},
														"1": {
															fields: [
																{
																	layout: {
																		leaf: {
																			key: "0x00000000",
																			ty: 2
																		}
																	},
																	name: "0"
																}
															],
															name: "Some"
														}
													}
												}
											},
											name: "name"
										},
										{
											layout: {
												"enum": {
													dispatchKey: "0x00000000",
													name: "Option",
													variants: {
														"0": {
															fields: [
															],
															name: "None"
														},
														"1": {
															fields: [
																{
																	layout: {
																		leaf: {
																			key: "0x00000000",
																			ty: 2
																		}
																	},
																	name: "0"
																}
															],
															name: "Some"
														}
													}
												}
											},
											name: "symbol"
										},
										{
											layout: {
												leaf: {
													key: "0x00000000",
													ty: 3
												}
											},
											name: "decimals"
										},
										{
											layout: {
												"enum": {
													dispatchKey: "0x00000000",
													name: "Option",
													variants: {
														"0": {
															fields: [
															],
															name: "None"
														},
														"1": {
															fields: [
																{
																	layout: {
																		leaf: {
																			key: "0x00000000",
																			ty: 1
																		}
																	},
																	name: "0"
																}
															],
															name: "Some"
														}
													}
												}
											},
											name: "_reserved"
										}
									],
									name: "Data"
								}
							},
							name: "metadata"
						}
					],
					name: "MyToken"
				}
			},
			root_key: "0x00000000"
		}
	},
	types: [
		{
			id: 0,
			type: {
				def: {
					primitive: "u128"
				}
			}
		},
		{
			id: 1,
			type: {
				def: {
					tuple: [
					]
				}
			}
		},
		{
			id: 2,
			type: {
				def: {
					sequence: {
						type: 3
					}
				}
			}
		},
		{
			id: 3,
			type: {
				def: {
					primitive: "u8"
				}
			}
		},
		{
			id: 4,
			type: {
				def: {
					variant: {
						variants: [
							{
								index: 0,
								name: "None"
							},
							{
								fields: [
									{
										type: 2
									}
								],
								index: 1,
								name: "Some"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 2
					}
				],
				path: [
					"Option"
				]
			}
		},
		{
			id: 5,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 1
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 1
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 6,
			type: {
				def: {
					variant: {
						variants: [
							{
								index: 1,
								name: "CouldNotReadInput"
							}
						]
					}
				},
				path: [
					"ink_primitives",
					"LangError"
				]
			}
		},
		{
			id: 7,
			type: {
				def: {
					composite: {
						fields: [
							{
								type: 8,
								typeName: "[u8; 32]"
							}
						]
					}
				},
				path: [
					"ink_primitives",
					"types",
					"AccountId"
				]
			}
		},
		{
			id: 8,
			type: {
				def: {
					array: {
						len: 32,
						type: 3
					}
				}
			}
		},
		{
			id: 9,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 0
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 0
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 10,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 11
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 11
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 11,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 1
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 12
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 1
					},
					{
						name: "E",
						type: 12
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 12,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 2,
										typeName: "String"
									}
								],
								index: 0,
								name: "Custom"
							},
							{
								index: 1,
								name: "InsufficientBalance"
							},
							{
								index: 2,
								name: "InsufficientAllowance"
							},
							{
								index: 3,
								name: "ZeroRecipientAddress"
							},
							{
								index: 4,
								name: "ZeroSenderAddress"
							},
							{
								fields: [
									{
										type: 2,
										typeName: "String"
									}
								],
								index: 5,
								name: "SafeTransferCheckFailed"
							}
						]
					}
				},
				path: [
					"openbrush_contracts",
					"traits",
					"errors",
					"psp22",
					"PSP22Error"
				]
			}
		},
		{
			id: 13,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 4
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 4
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 14,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 3
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 3
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		}
	],
	version: "4"
};

const makeContractCaller = ({
  chainConnector,
  chainId,
  registry
}) => async (callFrom, contractAddress, inputData) => registry.createType("ContractExecResult", await chainConnector.send(chainId, "state_call", ["ContractsApi_call", u8aToHex(u8aConcatStrict([
// origin
registry.createType("AccountId", callFrom).toU8a(),
// dest
registry.createType("AccountId", contractAddress).toU8a(),
// value
registry.createType("Balance", 0).toU8a(),
// gasLimit
registry.createType("Option<WeightV2>").toU8a(),
// storageDepositLimit
registry.createType("Option<Balance>").toU8a(),
// inputData
inputData.toU8a()]))]));

const subPsp22TokenId = (chainId, tokenSymbol) => `${chainId}-substrate-psp22-${tokenSymbol}`.toLowerCase().replace(/ /g, "-");
const getChainIdFromTokenId$1 = tokenId => {
  const match = /^([\d\w-]+)-substrate-psp22/.exec(tokenId);
  if (!match?.[1]) throw new Error(`Can't detect chainId for token ${tokenId}`);
  return match?.[1];
};
const SubPsp22Module = hydrate => {
  const {
    chainConnectors,
    chaindataProvider
  } = hydrate;
  const chainConnector = chainConnectors.substrate;
  assert(chainConnector, "This module requires a substrate chain connector");
  return {
    ...DefaultBalanceModule("substrate-psp22"),
    async fetchSubstrateChainMeta(chainId) {
      const isTestnet = (await chaindataProvider.chainById(chainId))?.isTestnet || false;
      return {
        isTestnet
      };
    },
    async fetchSubstrateChainTokens(chainId, chainMeta, moduleConfig) {
      const {
        isTestnet
      } = chainMeta;
      const registry = new TypeRegistry();
      const Psp22Abi = new Abi(psp22Abi);

      // TODO: Use `decodeOutput` from `./util/decodeOutput`
      const contractCall = makeContractCaller({
        chainConnector,
        chainId,
        registry
      });
      const tokens = {};
      for (const tokenConfig of moduleConfig?.tokens ?? []) {
        try {
          let symbol = tokenConfig?.symbol ?? "Unit";
          let decimals = tokenConfig?.decimals ?? 0;
          const existentialDeposit = tokenConfig?.ed ?? "0";
          const contractAddress = tokenConfig?.contractAddress ?? undefined;
          if (contractAddress === undefined) continue;
          await (async () => {
            const [symbolResult, decimalsResult] = await Promise.all([contractCall(contractAddress, contractAddress, registry.createType("Vec<u8>", Psp22Abi.findMessage("PSP22Metadata::token_symbol").selector)), contractCall(contractAddress, contractAddress, registry.createType("Vec<u8>", Psp22Abi.findMessage("PSP22Metadata::token_decimals").selector))]);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const symbolData = symbolResult.toJSON()?.result?.ok?.data;
            symbol = typeof symbolData === "string" && symbolData.startsWith("0x") ? u8aToString(registry.createType("Option<Vec<u8>>",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            symbolResult.toJSON()?.result?.ok?.data)?.value)?.replace(/\p{C}/gu, "") : symbol;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const decimalsData = decimalsResult.toJSON()?.result?.ok?.data;
            decimals = typeof decimalsData === "string" && decimalsData.startsWith("0x") ? hexToNumber(decimalsData) : decimals;
          })();
          const id = subPsp22TokenId(chainId, symbol);
          const token = {
            id,
            type: "substrate-psp22",
            isTestnet,
            isDefault: tokenConfig.isDefault ?? true,
            symbol,
            decimals,
            logo: tokenConfig?.logo || githubTokenLogoUrl(id),
            existentialDeposit,
            contractAddress,
            chain: {
              id: chainId
            }
          };
          if (tokenConfig?.symbol) token.symbol = tokenConfig?.symbol;
          if (tokenConfig?.coingeckoId) token.coingeckoId = tokenConfig?.coingeckoId;
          if (tokenConfig?.dcentName) token.dcentName = tokenConfig?.dcentName;
          if (tokenConfig?.mirrorOf) token.mirrorOf = tokenConfig?.mirrorOf;
          tokens[token.id] = token;
        } catch (error) {
          log.error(`Failed to build substrate-psp22 token ${tokenConfig.contractAddress} (${tokenConfig.symbol}) on ${chainId}`, error?.message ?? error);
          continue;
        }
      }
      return tokens;
    },
    getPlaceholderBalance(tokenId, address) {
      const chainId = getChainIdFromTokenId$1(tokenId);
      return {
        source: "substrate-psp22",
        status: "initializing",
        address,
        multiChainId: {
          subChainId: chainId
        },
        chainId,
        tokenId,
        free: "0"
      };
    },
    // TODO: Don't create empty subscriptions
    async subscribeBalances(addressesByToken, callback) {
      let subscriptionActive = true;
      const subscriptionInterval = 12_000; // 12_000ms == 12 seconds
      const initDelay = 3_000; // 3000ms == 3 seconds
      const cache = new Map();
      const tokens = await chaindataProvider.tokensById();
      const poll = async () => {
        if (!subscriptionActive) return;
        try {
          assert(chainConnectors.substrate, "This module requires a substrate chain connector");
          const balances = await fetchBalances(chainConnectors.substrate, tokens, addressesByToken);

          // Don't call callback with balances which have not changed since the last poll.
          const updatedBalances = new Balances([...balances].filter(b => {
            if (isEqual(cache.get(b.id), b.toJSON())) return false;
            cache.set(b.id, b.toJSON());
            return true;
          }));
          callback(null, updatedBalances);
        } catch (error) {
          callback(error);
        } finally {
          setTimeout(poll, subscriptionInterval);
        }
      };
      setTimeout(poll, initDelay);
      return () => {
        subscriptionActive = false;
      };
    },
    async fetchBalances(addressesByToken) {
      assert(chainConnectors.substrate, "This module requires a substrate chain connector");
      const tokens = await chaindataProvider.tokensById();
      return fetchBalances(chainConnectors.substrate, tokens, addressesByToken);
    },
    async transferToken({
      tokenId,
      from,
      to,
      amount,
      registry,
      metadataRpc,
      blockHash,
      blockNumber,
      nonce,
      specVersion,
      transactionVersion,
      tip
    }) {
      const token = await chaindataProvider.tokenById(tokenId);
      assert(token, `Token ${tokenId} not found in store`);
      if (token.type !== "substrate-psp22") throw new Error(`This module doesn't handle tokens of type ${token.type}`);
      const chainId = token.chain.id;
      const chain = await chaindataProvider.chainById(chainId);
      assert(chain?.genesisHash, `Chain ${chainId} not found in store`);
      const {
        genesisHash
      } = chain;
      const Psp22Abi = new Abi(psp22Abi);

      // TODO: Use `decodeOutput` from `./util/decodeOutput`
      const contractCall = makeContractCaller({
        chainConnector,
        chainId,
        registry
      });
      const data = registry.createType("Vec<u8>", Psp22Abi.findMessage("PSP22::transfer").toU8a([
      // TO
      to,
      // VALUE
      amount,
      // DATA
      undefined]));
      const dryRunResult = await contractCall(from, token.contractAddress, data);
      const pallet = "contracts";
      const method = "call";
      const args = {
        dest: token.contractAddress,
        value: 0,
        gasLimit: dryRunResult.gasRequired.toHex(),
        storageDepositLimit: dryRunResult.storageDeposit.isCharge ? dryRunResult.storageDeposit.asCharge.toHex() : null,
        data: data.toHex()
      };
      const unsigned = defineMethod({
        method: {
          pallet,
          name: method,
          args
        },
        address: from,
        blockHash,
        blockNumber,
        eraPeriod: 64,
        genesisHash,
        metadataRpc,
        nonce,
        specVersion,
        tip: tip ? Number(tip) : 0,
        transactionVersion
      }, {
        metadataRpc,
        registry
      });
      return {
        type: "substrate",
        tx: unsigned
      };
    }
  };
};
const fetchBalances = async (chainConnector, tokens, addressesByToken) => {
  const registry = new TypeRegistry();
  const Psp22Abi = new Abi(psp22Abi);
  const balanceRequests = Object.entries(addressesByToken).flatMap(([tokenId, addresses]) => addresses.map(address => [tokenId, address])).flatMap(async ([tokenId, address]) => {
    const token = tokens[tokenId];
    if (!token) {
      log.debug(`Token ${tokenId} not found`);
      return [];
    }
    if (token.type !== "substrate-psp22") {
      log.debug(`This module doesn't handle tokens of type ${token.type}`);
      return [];
    }

    // TODO: Use `decodeOutput` from `./util/decodeOutput`
    const contractCall = makeContractCaller({
      chainConnector,
      chainId: token.chain.id,
      registry
    });
    if (token.contractAddress === undefined) {
      log.debug(`Token ${tokenId} of type substrate-psp22 doesn't have a contractAddress`);
      return [];
    }
    const result = await contractCall(address, token.contractAddress, registry.createType("Vec<u8>", Psp22Abi.findMessage("PSP22::balance_of").toU8a([
    // ACCOUNT
    address])));
    const balance = registry
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .createType("Balance", hexToU8a(result.toJSON()?.result?.ok?.data).slice(1)).toString();
    return new Balance({
      source: "substrate-psp22",
      status: "live",
      address,
      multiChainId: {
        subChainId: token.chain.id
      },
      chainId: token.chain.id,
      tokenId,
      free: balance
    });
  });

  // wait for balance fetches to complete
  const balanceResults = await Promise.allSettled(balanceRequests);

  // filter out errors
  const balances = balanceResults.map(result => {
    if (result.status === "rejected") {
      log.debug(result.reason);
      return false;
    }
    return result.value;
  }).filter(balance => balance !== false);

  // return to caller
  return new Balances(balances);
};

const subTokensTokenId = (chainId, tokenSymbol) => `${chainId}-substrate-tokens-${tokenSymbol}`.toLowerCase().replace(/ /g, "-");
const getChainIdFromTokenId = tokenId => {
  const match = /^([\d\w-]+)-substrate-tokens/.exec(tokenId);
  if (!match?.[1]) throw new Error(`Can't detect chainId for token ${tokenId}`);
  return match?.[1];
};
const SubTokensModule = hydrate => {
  const {
    chainConnectors,
    chaindataProvider
  } = hydrate;
  const chainConnector = chainConnectors.substrate;
  assert(chainConnector, "This module requires a substrate chain connector");
  const {
    getOrCreateTypeRegistry
  } = createTypeRegistryCache();
  return {
    ...DefaultBalanceModule("substrate-tokens"),
    async fetchSubstrateChainMeta(chainId, moduleConfig, metadataRpc) {
      const isTestnet = (await chaindataProvider.chainById(chainId))?.isTestnet || false;
      if (metadataRpc === undefined) return {
        isTestnet,
        miniMetadata: null,
        metadataVersion: 0
      };
      const metadataVersion = getMetadataVersion(metadataRpc);
      if ((moduleConfig?.tokens ?? []).length < 1) return {
        isTestnet,
        miniMetadata: null,
        metadataVersion
      };
      if (metadataVersion !== 14) return {
        isTestnet,
        miniMetadata: null,
        metadataVersion
      };
      const metadata = $metadataV14.decode($.decodeHex(metadataRpc));
      const isTokensPallet = pallet => pallet.name === "Tokens";
      const isAccountsItem = item => item.name === "Accounts";

      // TODO: Handle metadata v15
      filterMetadataPalletsAndItems(metadata, [{
        pallet: isTokensPallet,
        items: [isAccountsItem]
      }]);
      metadata.extrinsic.signedExtensions = [];
      const miniMetadata = $.encodeHexPrefixed($metadataV14.encode(metadata));
      return {
        isTestnet,
        miniMetadata,
        metadataVersion
      };
    },
    async fetchSubstrateChainTokens(chainId, chainMeta, moduleConfig) {
      const {
        isTestnet
      } = chainMeta;
      const tokens = {};
      for (const tokenConfig of moduleConfig?.tokens ?? []) {
        try {
          const symbol = tokenConfig?.symbol ?? "Unit";
          const decimals = tokenConfig?.decimals ?? 0;
          const existentialDeposit = tokenConfig?.ed ?? "0";
          const onChainId = tokenConfig?.onChainId ?? undefined;
          if (onChainId === undefined) continue;
          const id = subTokensTokenId(chainId, symbol);
          const token = {
            id,
            type: "substrate-tokens",
            isTestnet,
            isDefault: tokenConfig.isDefault ?? true,
            symbol,
            decimals,
            logo: tokenConfig?.logo || githubTokenLogoUrl(id),
            existentialDeposit,
            onChainId,
            chain: {
              id: chainId
            }
          };
          if (tokenConfig?.symbol) token.symbol = tokenConfig?.symbol;
          if (tokenConfig?.coingeckoId) token.coingeckoId = tokenConfig?.coingeckoId;
          if (tokenConfig?.dcentName) token.dcentName = tokenConfig?.dcentName;
          if (tokenConfig?.mirrorOf) token.mirrorOf = tokenConfig?.mirrorOf;
          tokens[token.id] = token;
        } catch (error) {
          log.error(`Failed to build substrate-tokens token ${tokenConfig.onChainId} (${tokenConfig.symbol}) on ${chainId}`, error?.message ?? error);
          continue;
        }
      }
      return tokens;
    },
    getPlaceholderBalance(tokenId, address) {
      const chainId = getChainIdFromTokenId(tokenId);
      return {
        source: "substrate-tokens",
        status: "initializing",
        address,
        multiChainId: {
          subChainId: chainId
        },
        chainId,
        tokenId,
        free: "0",
        locks: "0",
        reserves: "0"
      };
    },
    // TODO: Don't create empty subscriptions
    async subscribeBalances(addressesByToken, callback) {
      const queries = await buildQueries(chaindataProvider, getOrCreateTypeRegistry, addressesByToken);
      const unsubscribe = await new RpcStateQueryHelper(chainConnector, queries).subscribe((error, result) => error ? callback(error) : callback(null, new Balances(result ?? [])));
      return unsubscribe;
    },
    async fetchBalances(addressesByToken) {
      assert(chainConnectors.substrate, "This module requires a substrate chain connector");
      const queries = await buildQueries(chaindataProvider, getOrCreateTypeRegistry, addressesByToken);
      const result = await new RpcStateQueryHelper(chainConnectors.substrate, queries).fetch();
      return new Balances(result ?? []);
    },
    async transferToken({
      tokenId,
      from,
      to,
      amount,
      registry,
      metadataRpc,
      blockHash,
      blockNumber,
      nonce,
      specVersion,
      transactionVersion,
      tip,
      transferMethod
    }) {
      const token = await chaindataProvider.tokenById(tokenId);
      assert(token, `Token ${tokenId} not found in store`);
      if (token.type !== "substrate-tokens") throw new Error(`This module doesn't handle tokens of type ${token.type}`);
      const chainId = token.chain.id;
      const chain = await chaindataProvider.chainById(chainId);
      assert(chain?.genesisHash, `Chain ${chainId} not found in store`);
      const {
        genesisHash
      } = chain;
      const currencyId = (() => {
        try {
          // `as string` doesn't matter here because we catch it if it throws
          return JSON.parse(token.onChainId);
        } catch (error) {
          return token.onChainId;
        }
      })();

      // different chains use different orml transfer methods
      // we'll try each one in sequence until we get one that doesn't throw an error
      let unsigned = undefined;
      const errors = [];
      const currenciesPallet = "currencies";
      const currenciesMethod = "transfer";
      const currenciesArgs = {
        dest: to,
        currencyId,
        amount
      };
      const sendAll = transferMethod === "transferAll";
      const tokensPallet = "tokens";
      const tokensMethod = transferMethod;
      const tokensArgs = sendAll ? {
        dest: to,
        currencyId,
        keepAlive: false
      } : {
        dest: to,
        currencyId,
        amount
      };
      const commonDefineMethodFields = {
        address: from,
        blockHash,
        blockNumber,
        eraPeriod: 64,
        genesisHash,
        metadataRpc,
        nonce,
        specVersion,
        tip: tip ? Number(tip) : 0,
        transactionVersion
      };
      const unsignedMethods = [() => defineMethod({
        method: {
          pallet: currenciesPallet,
          name: currenciesMethod,
          args: currenciesArgs
        },
        ...commonDefineMethodFields
      }, {
        metadataRpc,
        registry
      }), () => defineMethod({
        method: {
          pallet: tokensPallet,
          name: tokensMethod,
          args: tokensArgs
        },
        ...commonDefineMethodFields
      }, {
        metadataRpc,
        registry
      })];
      for (const method of unsignedMethods) {
        try {
          unsigned = method();
        } catch (error) {
          errors.push(error);
        }
      }
      if (unsigned === undefined) {
        errors.forEach(error => log.error(error));
        throw new Error(`${token.symbol} transfers are not supported at this time.`);
      }
      return {
        type: "substrate",
        tx: unsigned
      };
    }
  };
};
async function buildQueries(chaindataProvider, getOrCreateTypeRegistry, addressesByToken) {
  const chains = await chaindataProvider.chainsById();
  const tokens = await chaindataProvider.tokensById();
  const miniMetadatas = new Map((await db.miniMetadatas.toArray()).map(miniMetadata => [miniMetadata.id, miniMetadata]));
  const uniqueChainIds = getUniqueChainIds(addressesByToken, tokens);
  const chainStorageDecoders = buildStorageDecoders({
    chainIds: uniqueChainIds,
    chains,
    miniMetadatas,
    moduleType: "substrate-tokens",
    decoders: {
      storageDecoder: ["tokens", "accounts"]
    }
  });
  return Object.entries(addressesByToken).flatMap(([tokenId, addresses]) => {
    const token = tokens[tokenId];
    if (!token) {
      log.warn(`Token ${tokenId} not found`);
      return [];
    }
    if (token.type !== "substrate-tokens") {
      log.debug(`This module doesn't handle tokens of type ${token.type}`);
      return [];
    }
    const chainId = token.chain?.id;
    if (!chainId) {
      log.warn(`Token ${tokenId} has no chain`);
      return [];
    }
    const chain = chains[chainId];
    if (!chain) {
      log.warn(`Chain ${chainId} for token ${tokenId} not found`);
      return [];
    }
    const [chainMeta] = findChainMeta(miniMetadatas, "substrate-tokens", chain);
    const registry = chainMeta?.miniMetadata !== undefined && chainMeta?.miniMetadata !== null && chainMeta?.metadataVersion >= 14 ? getOrCreateTypeRegistry(chainId, chainMeta.miniMetadata) : new TypeRegistry();
    return addresses.flatMap(address => {
      const storageHelper = new StorageHelper(registry, "tokens", "accounts", decodeAnyAddress(address), (() => {
        try {
          // `as string` doesn't matter here because we catch it if it throws
          return JSON.parse(token.onChainId);
        } catch (error) {
          return token.onChainId;
        }
      })());
      const storageDecoder = chainStorageDecoders.get(chainId)?.storageDecoder;
      const stateKey = storageHelper.stateKey;
      if (!stateKey) return [];
      const decodeResult = change => {
        // e.g.
        // {
        //   free: 33,765,103,752,560n
        //   reserved: 0n
        //   frozen: 0n
        // }
        const balance = (storageDecoder && change !== null ? storageDecoder.decode($.decodeHex(change)) :
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        null) ?? {
          free: 0n,
          reserved: 0n,
          frozen: 0n
        };
        const free = (balance?.free ?? 0n).toString();
        const reserved = (balance?.reserved ?? 0n).toString();
        const frozen = (balance?.frozen ?? 0n).toString();
        return new Balance({
          source: "substrate-tokens",
          status: "live",
          address,
          multiChainId: {
            subChainId: chainId
          },
          chainId,
          tokenId: token.id,
          free,
          reserves: reserved,
          locks: frozen
        });
      };
      return {
        chainId,
        stateKey,
        decodeResult
      };
    });
  });
}

const defaultBalanceModules = [EvmErc20Module, EvmNativeModule, SubAssetsModule, SubEquilibriumModule, SubNativeModule, SubPsp22Module, SubTokensModule];

/** Pulls the latest chaindata from https://github.com/TalismanSociety/chaindata */
const hydrateChaindataAndMiniMetadata = async (chaindataProvider, miniMetadataUpdater) => {
  await Promise.all([miniMetadataUpdater.hydrateFromChaindata(), miniMetadataUpdater.hydrateCustomChains(), chaindataProvider.hydrateChains(), chaindataProvider.hydrateEvmNetworks()]);
  const chains = await chaindataProvider.chains();
  const {
    statusesByChain
  } = await miniMetadataUpdater.statuses(chains);
  const goodChains = [...statusesByChain.entries()].flatMap(([chainId, status]) => status === "good" ? chainId : []);
  await chaindataProvider.hydrateTokens(goodChains);
};

/** Builds any missing miniMetadatas (e.g. for the user's custom substrate chains) */
const updateCustomMiniMetadata = async (chaindataProvider, miniMetadataUpdater) => {
  const chainIds = await chaindataProvider.chainIds();
  await miniMetadataUpdater.update(chainIds);
};

/** Fetches any missing Evm Tokens */
const updateEvmTokens = async (chaindataProvider, evmTokenFetcher) => {
  const evmNetworkIds = await chaindataProvider.evmNetworkIds();
  await evmTokenFetcher.update(evmNetworkIds);
};

export { Balance, BalanceFormatter, BalanceStatusLive, Balances, DefaultBalanceModule, EvmErc20Module, EvmNativeModule, EvmTokenFetcher, FiatSumBalancesFormatter, MiniMetadataUpdater, PlanckSumBalancesFormatter, RpcStateQueryHelper, StorageHelper, SubAssetsModule, SubEquilibriumModule, SubNativeModule, SubPsp22Module, SubTokensModule, SumBalancesFormatter, TalismanBalancesDatabase, asObservable, balances, buildStorageDecoders, createSubscriptionId, createTypeRegistryCache, crowdloanFundContributionsChildKey, db, defaultBalanceModules, deleteSubscriptionId, deriveMiniMetadataId, deriveStatuses, detectTransferMethod, erc20Abi, evmErc20TokenId, evmNativeTokenId, excludeFromFeePayableLocks, excludeFromTransferableAmount, filterBaseLocks, filterMirrorTokens, findChainMeta, getLockTitle, getLockedType, getUniqueChainIds, getValidSubscriptionIds, hydrateChaindataAndMiniMetadata, includeInTotalExtraAmount, nompoolRewardAccountId, nompoolStashAccountId, subNativeTokenId, subscribeNompoolStaking, subshapeStorageDecoder, updateCustomMiniMetadata, updateEvmTokens };
